import React, { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { sanitizeErrorForLog, getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';
import TopNavigation from './TopNavigation';
import BottomNavigation from './BottomNavigation';
import './UserPage.css';
import './PostFeed.css';
import '../styles/buttons.css';
// No longer needed
import BiohackInfoCard from './BiohackInfoCard';
import XPProgressBar from './XPProgressBar';
import { useNavigate } from 'react-router-dom';
import PostCard from './PostCard';
import { fetchBiomarkerAttachments } from './PostFeed';
import ProtocolShield from './ProtocolShield';
import { Icon } from './Icon.js';
import ProfileCard from './ProfileCard';
import UserProfilePage from './UserProfilePage';
import LabsTab from './LabsTab';
import ChallengesTab from './ChallengesTab';
import WearablesTab from './WearablesTab';
import ScheduleSlideOver from './ScheduleSlideOver';
import ProtocolNotesSlideOver from './ProtocolNotesSlideOver';
import JellyfishLoader from './JellyfishLoader';
import { useProtocolActions } from '../hooks/useProtocolActions';
import { useUserProtocolStatus } from '../contexts/UserProtocolStatusContext';
import ReactECharts from 'echarts-for-react';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../utils/imageUtils';
import Onboarding from './Onboarding';
import { useOnboarding } from '../hooks/useOnboarding';
import { evaluateChallengeTarget } from '../utils/challengeTarget';

// Dummy data for protocols and activity
const protocols = [
  { 
    id: '1', 
    name: "Cold Therapy Protocol", 
    description: "Daily cold exposure for improved recovery and metabolism",
    status: 'staged',
    hacks: [
      { id: 1, name: "Cold Shower", image_url: "/default-biohack.jpg", ease_of_use_score: 4.2, time_efficiency_score: 3.8, affordability_score: 5.0 },
      { id: 2, name: "Ice Bath", image_url: "/default-biohack.jpg", ease_of_use_score: 3.5, time_efficiency_score: 4.0, affordability_score: 4.5 }
    ]
  },
  { 
    id: '2', 
    name: "Breathing Protocol", 
    description: "Daily breathing exercises for stress reduction and focus",
    status: 'active',
    activated_at: '2024-01-15T10:30:00Z',
    hacks: [
      { id: 3, name: "Box Breathing", image_url: "/default-biohack.jpg", ease_of_use_score: 4.8, time_efficiency_score: 4.5, affordability_score: 5.0 },
      { id: 4, name: "Wim Hof Method", image_url: "/default-biohack.jpg", ease_of_use_score: 4.0, time_efficiency_score: 3.5, affordability_score: 5.0 }
    ]
  }
];
// Generate dummy activity for the last 30 days
function getLastNDaysActivity(n, protocols) {
  const today = new Date();
  const arr = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - (n - 1 - i));
    const dateStr = d.toISOString().slice(0, 10);
    // Always include at least one protocol, and randomly add the other
    const ids = [protocols[0].id];
    if (Math.random() > 0.5) ids.push(protocols[1].id);
    arr.push({ date: dateStr, protocols: ids });
  }
  return arr;
}
const dummyActivity = getLastNDaysActivity(60, protocols);
// Helper function to get CSS variable value (defined later in file, but referenced here)
const getCSSVar = (varName) => {
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
};

// Map color names to CSS variables from color-scheme.css
// All colors come from the single source of truth: color-scheme.css
const getColorFromScheme = (colorName) => {
  const colorMap = {
    'orange': getCSSVar('--color-warning'),
    'blue': getCSSVar('--color-primary'),
    'bronze': getCSSVar('--color-warning-dark'),
    'yellow': getCSSVar('--color-warning'),
  };
  return colorMap[colorName] || getCSSVar('--color-primary');
};

const dummyAchievements = [
  { id: 1, color: getColorFromScheme('orange'), icon: '🏆' },
  { id: 2, color: getColorFromScheme('orange'), icon: '🏆' },
  { id: 3, color: getColorFromScheme('orange'), icon: '🏆' },
  { id: 4, color: getColorFromScheme('blue'), icon: '🏆' },
  { id: 5, color: getColorFromScheme('bronze'), icon: '🏆' }
];
const dummyChallenges = [
  {
    id: 1,
    title: "Timmy's Weekly Challenge",
    label: "2/5 Cold Plunges",
    meta: "2 days left",
    progress: 0.4,
    color: getColorFromScheme('yellow')
  },
  {
    id: 2,
    title: "Jase's Scrotum Challenge",
    label: "2/5 Scrotum Plunges",
    meta: "3 days left",
    progress: 0.4,
    color: getColorFromScheme('blue')
  }
];
const dummyFollowers = [
  { id: 1, username: 'timmy', avatar: null },
  { id: 2, username: 'jase', avatar: null },
  { id: 3, username: 'sarah', avatar: null }
];
const dummyFollowing = [
  { id: 4, username: 'alex', avatar: null },
  { id: 5, username: 'maria', avatar: null }
];

// Dummy data for forum posts
const dummyForumPosts = [
  {
    id: 1,
    title: "Lion’s Mane",
    excerpt: "Lion's Mane keeps aging at bay...",
    timestamp: "2 days ago"
  },
  {
    id: 2,
    title: "Cold Plunge Tips",
    excerpt: "Here’s how I make it through the first 30 seconds...",
    timestamp: "2 days ago"
  }
];

function weightedAverage(protocol, key) {
  const hacks = protocol.hacks || [];
  if (!hacks.length) {
    return 0;
  }
  
  // Calculate average from biohack scores
  const validScores = hacks
    .map(hack => hack[key])
    .filter(score => score !== null && score !== undefined && !isNaN(score));
  
  if (validScores.length === 0) return 0;
  
  const sum = validScores.reduce((acc, score) => acc + score, 0);
  const average = sum / validScores.length;
  
  // Round to 1 decimal place
  return Math.round(average * 10) / 10;
}

function MetricBar({ label, value }) {
  return (
    <div className="protocol-metric-item">
      <div className="protocol-metric-label">{label}</div>
      <div className="protocol-metric-stars">
        {[...Array(5)].map((_, i) => (
          <Icon 
            key={i} 
            name="Star" 
            className={`icon--xs ${i < Math.round(value || 0) ? 'star-filled' : 'star-empty'}`}
            title={`${label}: ${(value || 0).toFixed(1)}/5.0`}
          />
        ))}
      </div>
    </div>
  );
}


function DayDetailsPanel({ selectedDay, protocols, biohacks, onClose, onBiohackClick, onProtocolClick, showAllBiohacks, setShowAllBiohacks }) {
  // Group activities by protocol
  const activitiesByProtocol = {};
  
  selectedDay.activities.forEach(activity => {
    // Handle different activity data structures
    const protocolData = activity.protocols || [];
    
    protocolData.forEach((biohackLog, index) => {
      // Handle different data structures
      let protocolId, hackId;
      
      if (biohackLog.protocol_id && biohackLog.hack_id) {
        // It's a biohack log entry with protocol_id and hack_id
        protocolId = biohackLog.protocol_id;
        hackId = biohackLog.hack_id;
      } else if (typeof biohackLog === 'string') {
        // It's just a protocol ID (old format)
        protocolId = biohackLog;
        hackId = null;
      } else if (typeof biohackLog === 'object' && biohackLog.id) {
        // It's a protocol object (old format)
        protocolId = biohackLog.id;
        hackId = null;
      } else {
        return;
      }
      
      // Find the protocol
      const protocol = protocols.find(p => p.id === protocolId);
      
      if (protocol) {
        if (!activitiesByProtocol[protocolId]) {
          activitiesByProtocol[protocolId] = {
            protocol: protocol,
            biohacks: []
          };
        }
        
        if (hackId) {
          // Find the specific biohack that was logged - use full biohack data from biohacks prop
          const fullBiohack = biohacks?.find(b => b.id === hackId);
          if (fullBiohack) {
            // Avoid duplicates
            if (!activitiesByProtocol[protocolId].biohacks.find(b => b.id === fullBiohack.id)) {
              activitiesByProtocol[protocolId].biohacks.push(fullBiohack);
            }
          } else {
            // Fallback to protocol hack if full biohack not found
            const specificHack = protocol.hacks?.find(h => h.id === hackId);
            if (specificHack) {
              if (!activitiesByProtocol[protocolId].biohacks.find(b => b.id === specificHack.id)) {
                activitiesByProtocol[protocolId].biohacks.push(specificHack);
              }
            }
          }
        } else {
          // Old format - add all biohacks from the protocol
          if (protocol.hacks && protocol.hacks.length > 0) {
            protocol.hacks.forEach(hack => {
              // Try to find full biohack data first
              const fullBiohack = biohacks?.find(b => b.id === hack.id);
              const biohackToAdd = fullBiohack || hack;
              // Avoid duplicates
              if (!activitiesByProtocol[protocolId].biohacks.find(b => b.id === biohackToAdd.id)) {
                activitiesByProtocol[protocolId].biohacks.push(biohackToAdd);
              }
            });
          }
        }
      }
    });
  });

  // Calculate total biohacks for the selected day
  const totalBiohacks = Object.values(activitiesByProtocol).reduce((total, { biohacks }) => total + biohacks.length, 0);

  return (
    <>
      <div className="log-header">
        <div className="log-title">
          {selectedDay.date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
      </div>
      
      <div className="log-entries-container">
        {Object.keys(activitiesByProtocol).length === 0 ? (
          <div className="log-empty-state">
            <div className="empty-icon">📊</div>
            <div className="empty-text">No activities logged</div>
            <div className="empty-subtext">Start logging biohacks to see them here</div>
          </div>
        ) : (
          <>
            <div className="modern-log-entry">
              <div className="log-entry-content">
                <div className="log-entry-text">
                  <span className="log-username">You</span> logged <span className="log-count">{totalBiohacks}</span> biohack{totalBiohacks !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
            
            <div className="biohack-list">
              {(() => {
                // Flatten all biohacks from all protocols
                const allBiohacks = Object.values(activitiesByProtocol).flatMap(({ protocol, biohacks }) => 
                  biohacks.map((hack, index) => ({ ...hack, protocol, originalIndex: index }))
                );
                
                // Limit to 4 biohacks initially, or show all if showAllBiohacks is true
                const displayBiohacks = showAllBiohacks ? allBiohacks : allBiohacks.slice(0, 4);
                
                return (
                  <>
                    {displayBiohacks.map((hack, index) => (
                      <div key={`${hack.protocol.id}-${hack.id}-${hack.originalIndex}`} className="biohack-image-container">
                        <img 
                          src={hack.image_url || '/default-biohack.jpg'} 
                          alt={hack.name} 
                          className="biohack-icon" 
                          onClick={() => onBiohackClick && onBiohackClick(hack)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span 
                          className="biohack-name"
                          onClick={() => onBiohackClick && onBiohackClick(hack)}
                          style={{ cursor: 'pointer' }}
                        >
                          {hack.name}
                        </span>
                        <span 
                          className="biohack-protocol-tag"
                          onClick={() => onProtocolClick && onProtocolClick(hack.protocol)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Icon name="TrendingUp" className="icon--xs" />
                          {hack.protocol.name}
                        </span>
                      </div>
                    ))}
                    {allBiohacks.length > 4 && (
                      <div className="biohack-show-more">
                        <button 
                          className="btn btn--secondary btn--sm"
                          onClick={() => setShowAllBiohacks(!showAllBiohacks)}
                        >
                          {showAllBiohacks ? 'Show Less' : `Show More (${allBiohacks.length - 4} more)`}
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Helper function to get CSS variable value (moved to top, but keep here for backward compatibility)
// Note: getCSSVar is also defined at the top of the file for use in dummy data

// Component for concentric rings visualization
function BiohackRingsView({ biohacks, onRingHover, hoveredRingIndex, onRingClick, selectedRingIndex }) {
  const ringColors = [
    getCSSVar('--color-primary-light'),
    getCSSVar('--color-primary'),
    getCSSVar('--color-success-light'),
    getCSSVar('--color-warning-light'),
    getCSSVar('--color-accent-light'),
    getCSSVar('--color-info-light'),
    getCSSVar('--color-primary-dark'),
    getCSSVar('--color-accent'),
  ];

  // Reverse the order so largest rings are on the outside
  const reversedBiohacks = [...biohacks].reverse();

  return (
    <div className="biohack-rings-container">
      <div className="biohack-rings-wrapper">
        {reversedBiohacks.map((biohack, reversedIndex) => {
          const originalIndex = biohacks.length - 1 - reversedIndex;
          const ringSize = 80 + (reversedIndex * 50); // Increasing ring sizes from inside out
          const isHovered = hoveredRingIndex === originalIndex;
          const isSelected = selectedRingIndex === originalIndex;
          const color = ringColors[originalIndex % ringColors.length];
          // Inner rings should have higher z-index so they can be clicked
          const zIndex = reversedBiohacks.length - reversedIndex;
          
          return (
            <div
              key={biohack.id || originalIndex}
              className={`biohack-ring ${isHovered ? 'ring-hovered' : ''} ${isSelected ? 'ring-selected' : ''}`}
              style={{
                width: `${ringSize}px`,
                height: `${ringSize}px`,
                borderColor: color,
                borderWidth: '12px', // Thicker border for easier clicking
                backgroundColor: isSelected ? `${color}25` : (isHovered ? `${color}15` : 'transparent'),
                zIndex: zIndex,
              }}
              onMouseEnter={() => onRingHover && onRingHover(originalIndex)}
              onMouseLeave={() => onRingHover && onRingHover(null)}
              onClick={() => onRingClick && onRingClick(originalIndex)}
              title={biohack.name}
            >
              {(isHovered || isSelected) && (
                <div className="ring-label" style={{ color: color }}>
                  {biohack.name}
                </div>
              )}
              {reversedIndex === reversedBiohacks.length - 1 && (
                <div className="ring-center-content">
                  <div className="ring-center-dot" style={{ backgroundColor: ringColors[0] }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Component for biohack visualization
function BiohackVisualization({ biohack }) {
  if (!biohack) {
    return (
      <div className="biohack-visualization-empty">
        <div className="visualization-empty-text">Hover over a ring to see biohack details</div>
        <div className="visualization-empty-subtext">Each ring represents a biohack logged on this day</div>
      </div>
    );
  }

  return (
    <div className="biohack-visualization">
      <div className="biohack-visualization-header">
        <div className="biohack-visualization-image-wrapper">
          <img 
            src={biohack.image_url || '/default-biohack.jpg'} 
            alt={biohack.name}
            className="biohack-visualization-image"
            onError={(e) => {
              e.target.src = '/default-biohack.jpg';
            }}
          />
        </div>
        <div className="biohack-visualization-title">{biohack.name}</div>
      </div>
      
      <div className="biohack-visualization-content">
        {biohack.description && (
          <div className="biohack-visualization-description">
            {biohack.description}
          </div>
        )}
        
        <div className="biohack-visualization-stats">
          {biohack.ease_of_use_score !== null && biohack.ease_of_use_score !== undefined && (
            <div className="biohack-stat-item">
              <span className="stat-label">Ease of Use</span>
              <div className="stat-stars">
                {[...Array(5)].map((_, i) => (
                  <Icon 
                    key={i} 
                    name="Star" 
                    className={`icon--xs ${i < Math.round(biohack.ease_of_use_score || 0) ? 'star-filled' : 'star-empty'}`}
                  />
                ))}
                <span className="stat-value">{biohack.ease_of_use_score?.toFixed(1) || 'N/A'}</span>
              </div>
            </div>
          )}
          
          {biohack.time_efficiency_score !== null && biohack.time_efficiency_score !== undefined && (
            <div className="biohack-stat-item">
              <span className="stat-label">Time Efficiency</span>
              <div className="stat-stars">
                {[...Array(5)].map((_, i) => (
                  <Icon 
                    key={i} 
                    name="Star" 
                    className={`icon--xs ${i < Math.round(biohack.time_efficiency_score || 0) ? 'star-filled' : 'star-empty'}`}
                  />
                ))}
                <span className="stat-value">{biohack.time_efficiency_score?.toFixed(1) || 'N/A'}</span>
              </div>
            </div>
          )}
          
          {biohack.affordability_score !== null && biohack.affordability_score !== undefined && (
            <div className="biohack-stat-item">
              <span className="stat-label">Affordability</span>
              <div className="stat-stars">
                {[...Array(5)].map((_, i) => (
                  <Icon 
                    key={i} 
                    name="Star" 
                    className={`icon--xs ${i < Math.round(biohack.affordability_score || 0) ? 'star-filled' : 'star-empty'}`}
                  />
                ))}
                <span className="stat-value">{biohack.affordability_score?.toFixed(1) || 'N/A'}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityTimeline({ activity = [], protocols = [], biohacks = [], onDayClick, selectedDay, onBiohackClick, onProtocolClick, showAllBiohacks, setShowAllBiohacks, weekOffset = 0, onWeekOffsetChange, allUserProtocols = [], isOwnProfile = true, profileUserId = null }) {
  const [selectedBiohack, setSelectedBiohack] = useState(null);
  const [internalSelectedDay, setInternalSelectedDay] = useState(null);
  const [biohackChartData, setBiohackChartData] = useState({ weeks: [], counts: [] });
  const [chartLoading, setChartLoading] = useState(false);
  const [biohackStats, setBiohackStats] = useState({ totalActivities: 0, weeklyAverage: 0, mostActiveDay: 'N/A' });
  const [statsLoading, setStatsLoading] = useState(false);
  const sidebarRef = useRef(null);
  
  // Get the user ID to use for fetching activity data (profile user if viewing other user's profile, current user if own profile)
  const getTargetUserId = useCallback(async () => {
    if (profileUserId) {
      return profileUserId;
    }
    // Fallback to current user if profileUserId not provided (for backward compatibility)
    const cacheKey = getCacheKey('getCurrentUser', []);
    const user = await dedupeRequest(
      cacheKey,
      async () => {
        const { data: { user } = {}, error } = await supabase.auth.getUser();
        if (error) {
          console.error('Error fetching current user:', sanitizeErrorForLog(error));
          return null;
        }
        return user;
      },
      { keepAliveMs: 5000 }
    );
    return user?.id || null;
  }, [profileUserId]);
  
  // Show 4 weeks (28 days) with offset support
  const daysInGrid = 28;
  const latestActivityDate = new Date();
  
  // Calculate the start date based on week offset
  const startDate = new Date(latestActivityDate);
  startDate.setDate(latestActivityDate.getDate() - (daysInGrid - 1) - (weekOffset * daysInGrid));
  
  // Generate 28 days starting from the calculated start date
  const days = Array.from({ length: daysInGrid }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return d;
  });
  
  // Map activity by date string
  const activityMap = {};
  (activity || []).forEach(a => { 
    activityMap[a.date] = a.protocols || []; 
  });
  
  // Find the most recent date with activity
  const findMostRecentActivityDate = () => {
    if (!activity || activity.length === 0) return null;
    
    // Get all dates with activity, sorted by date (most recent first)
    const datesWithActivity = activity
      .filter(a => a.protocols && a.protocols.length > 0)
      .map(a => a.date)
      .sort((a, b) => new Date(b) - new Date(a));
    
    if (datesWithActivity.length === 0) return null;
    
    const mostRecentDateStr = datesWithActivity[0];
    const mostRecentDate = new Date(mostRecentDateStr);
    return { dateStr: mostRecentDateStr, date: mostRecentDate };
  };
  
  // Initialize with most recent activity date or use selectedDay if provided
  useEffect(() => {
    if (selectedDay) {
      setInternalSelectedDay(selectedDay);
    }
  }, [selectedDay]);
  
  // Initialize with most recent activity date on mount or when activity changes
  useEffect(() => {
    if (!selectedDay && !internalSelectedDay && activity && activity.length > 0) {
      const mostRecent = findMostRecentActivityDate();
      if (mostRecent) {
        setInternalSelectedDay(mostRecent);
        // Also notify parent if onDayClick is provided
        if (onDayClick) {
          onDayClick(mostRecent.dateStr, mostRecent.date);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity]);
  
  // Use internal selected day if no external selectedDay is provided
  const activeSelectedDay = selectedDay || internalSelectedDay;
  
  // Get biohacks for selected day with protocol information
  // Returns one entry per biohack-protocol combination
  const getBiohacksForDay = (dateStr) => {
    const dayActivities = activity.filter(act => act.date === dateStr);
    const allBiohackEntries = [];
    const protocolsToUse = allUserProtocols.length > 0 ? allUserProtocols : protocols;
    
    dayActivities.forEach(activity => {
      const protocolData = activity.protocols || [];
      protocolData.forEach((biohackLog) => {
        let protocolId, hackId;
        
        if (biohackLog.protocol_id && biohackLog.hack_id) {
          protocolId = biohackLog.protocol_id;
          hackId = biohackLog.hack_id;
        } else {
          return;
        }
        
        // Find the biohack
        const fullBiohack = biohacks?.find(b => b.id === hackId);
        if (fullBiohack) {
          // Find the protocol
          const protocol = protocolsToUse.find(p => p.id === protocolId);
          const protocolName = protocol?.name || 'Unknown Protocol';
          
          // Create a unique entry for each biohack-protocol combination
          // Use a combination key to avoid duplicates
          const entryKey = `${hackId}-${protocolId}`;
          const existingIndex = allBiohackEntries.findIndex(
            entry => `${entry.id}-${entry.protocolId}` === entryKey
          );
          
          if (existingIndex === -1) {
            // Add new entry for this biohack-protocol combination
            allBiohackEntries.push({
              ...fullBiohack,
              protocolName: protocolName,
              protocolId: protocolId
            });
          }
        }
      });
    });
    
    return allBiohackEntries;
  };
  
  // Calculate total biohacks logged per day
  const getBiohackCountForDay = (dateStr) => {
    const protocolsForDay = activityMap[dateStr] || [];
    return protocolsForDay.length;
  };
  
  // Calculate total activities for the 4 weeks
  const totalActivities = days.reduce((total, date) => {
    const dateStr = date.toISOString().slice(0, 10);
    return total + getBiohackCountForDay(dateStr);
  }, 0);
  
  // Calculate weekly average
  const weeklyAverage = (totalActivities / 4).toFixed(1);
  
  // Find most active day
  let mostActiveDay = { day: 'N/A', count: 0 };
  days.forEach(date => {
    const dateStr = date.toISOString().slice(0, 10);
    const count = getBiohackCountForDay(dateStr);
    if (count > mostActiveDay.count) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      mostActiveDay = { day: dayName, count };
    }
  });
  
  // Group days by week (7 days per week)
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  
  // Generate dynamic day abbreviations
  const firstDay = days[0];
  const dayAbbreviations = [];
  for (let i = 0; i < 7; i++) {
    const dayIndex = (firstDay.getDay() + i) % 7;
    const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    dayAbbreviations.push(dayNames[dayIndex]);
  }
  
  // Calculate date range for display
  const getDateRangeDisplay = () => {
    if (weekOffset === 0) {
      return "Last Month";
    }
    
    const endDate = new Date(latestActivityDate);
    endDate.setDate(latestActivityDate.getDate() - (weekOffset * daysInGrid));
    
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - daysInGrid + 1);
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Get biohacks for selected day
  const selectedDayBiohacks = activeSelectedDay ? getBiohacksForDay(activeSelectedDay.dateStr) : [];
  
  // Reset selected biohack when day changes
  useEffect(() => {
    if (activeSelectedDay) {
      setSelectedBiohack(null);
    }
  }, [activeSelectedDay?.dateStr]);
  
  // Fetch biohack logging data grouped by weeks
  const fetchBiohackChartData = useCallback(async (biohackId) => {
    if (!biohackId) {
      setBiohackChartData({ weeks: [], counts: [] });
      return;
    }
    
    setChartLoading(true);
    try {
      const targetUserId = await getTargetUserId();
      if (!targetUserId) {
        setBiohackChartData({ weeks: [], counts: [] });
        return;
      }
      
      // Fetch all logs for this biohack with deduplication
      const chartCacheKey = getCacheKey('fetchBiohackChartData', [targetUserId, biohackId]);
      const logs = await dedupeRequest(chartCacheKey, async () => {
        const { data, error } = await supabase
          .from('logging_hack_entries')
          .select('date')
          .eq('user_id', targetUserId)
          .eq('hack_id', biohackId)
          .order('date', { ascending: true });
        if (error) throw error;
        return data;
      });
      
      if (!logs || logs.length === 0) {
        setBiohackChartData({ weeks: [], counts: [] });
        return;
      }
      
      // Group logs by week
      const weekMap = new Map();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weeksAgo = 12; // Show last 12 weeks
      
      // Get the start of the current week (Sunday)
      const currentWeekStart = new Date(today);
      currentWeekStart.setDate(today.getDate() - today.getDay());
      currentWeekStart.setHours(0, 0, 0, 0);
      
      // Initialize weeks map for the last 12 weeks
      for (let i = weeksAgo - 1; i >= 0; i--) {
        const weekStart = new Date(currentWeekStart);
        weekStart.setDate(currentWeekStart.getDate() - (i * 7));
        const weekKey = weekStart.toISOString().split('T')[0];
        weekMap.set(weekKey, { weekStart: new Date(weekStart), count: 0 });
      }
      
      // Count logs per week
      logs.forEach(log => {
        const logDate = new Date(log.date);
        logDate.setHours(0, 0, 0, 0);
        
        // Find the start of the week for this log (Sunday)
        const weekStart = new Date(logDate);
        weekStart.setDate(logDate.getDate() - logDate.getDay());
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (weekMap.has(weekKey)) {
          weekMap.get(weekKey).count++;
        }
      });
      
      // Convert to arrays for chart, sorted by date
      const weeks = [];
      const counts = [];
      
      Array.from(weekMap.entries())
        .sort((a, b) => new Date(a[0]) - new Date(b[0]))
        .forEach(([weekKey, { weekStart, count }]) => {
          const weekLabel = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          weeks.push(weekLabel);
          counts.push(count);
        });
      
      setBiohackChartData({ weeks, counts });
    } catch (error) {
      setBiohackChartData({ weeks: [], counts: [] });
    } finally {
      setChartLoading(false);
    }
  }, [getTargetUserId]);
  
  // Fetch biohack stats since inception
  const fetchBiohackStats = useCallback(async (biohackId) => {
    if (!biohackId) {
      setBiohackStats({ totalActivities: 0, weeklyAverage: 0, mostActiveDay: 'N/A' });
      return;
    }
    
    setStatsLoading(true);
    try {
      const targetUserId = await getTargetUserId();
      if (!targetUserId) {
        setBiohackStats({ totalActivities: 0, weeklyAverage: 0, mostActiveDay: 'N/A' });
        return;
      }
      
      // Fetch all logs for this biohack since inception with deduplication
      const statsCacheKey = getCacheKey('fetchBiohackStats', [targetUserId, biohackId]);
      const logs = await dedupeRequest(statsCacheKey, async () => {
        const { data, error } = await supabase
          .from('logging_hack_entries')
          .select('date')
          .eq('user_id', targetUserId)
          .eq('hack_id', biohackId)
          .order('date', { ascending: true });
        if (error) throw error;
        return data;
      });
      
      if (!logs || logs.length === 0) {
        setBiohackStats({ totalActivities: 0, weeklyAverage: 0, mostActiveDay: 'N/A' });
        return;
      }
      
      // Calculate stats
      const totalActivities = logs.length;
      
      // Find first and last log dates
      const firstLogDate = new Date(logs[0].date);
      const lastLogDate = new Date(logs[logs.length - 1].date);
      
      // Calculate weeks since first log
      const daysDiff = Math.ceil((lastLogDate - firstLogDate) / (1000 * 60 * 60 * 24));
      const weeksSinceInception = Math.max(1, Math.ceil(daysDiff / 7)); // At least 1 week
      const weeklyAverage = (totalActivities / weeksSinceInception).toFixed(1);
      
      // Find most active day of week
      const dayCounts = {
        0: 0, // Sunday
        1: 0, // Monday
        2: 0, // Tuesday
        3: 0, // Wednesday
        4: 0, // Thursday
        5: 0, // Friday
        6: 0  // Saturday
      };
      
      logs.forEach(log => {
        const logDate = new Date(log.date);
        const dayOfWeek = logDate.getDay();
        dayCounts[dayOfWeek]++;
      });
      
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      let mostActiveDay = 'N/A';
      let maxCount = 0;
      
      Object.entries(dayCounts).forEach(([day, count]) => {
        if (count > maxCount) {
          maxCount = count;
          mostActiveDay = dayNames[parseInt(day)];
        }
      });
      
      setBiohackStats({ totalActivities, weeklyAverage, mostActiveDay });
    } catch (error) {
      setBiohackStats({ totalActivities: 0, weeklyAverage: 0, mostActiveDay: 'N/A' });
    } finally {
      setStatsLoading(false);
    }
  }, [getTargetUserId]);
  
  // Load chart data when biohack is selected
  useEffect(() => {
    if (selectedBiohack?.id) {
      fetchBiohackChartData(selectedBiohack.id);
      fetchBiohackStats(selectedBiohack.id);
    } else {
      setBiohackChartData({ weeks: [], counts: [] });
      setBiohackStats({ totalActivities: 0, weeklyAverage: 0, mostActiveDay: 'N/A' });
    }
  }, [selectedBiohack?.id, fetchBiohackChartData, fetchBiohackStats]);
  
  // Handle day click
  const handleDayClick = (dateStr, date) => {
    const dayData = { dateStr, date };
    setInternalSelectedDay(dayData);
    if (onDayClick) {
      onDayClick(dateStr, date);
    }

    // On mobile, scroll to sidebar when a day is clicked
    if (window.innerWidth <= 768 && sidebarRef.current) {
      sidebarRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest', 
        inline: 'center' 
      });
    }
  };
  
  // Handle sidebar close
  const handleSidebarClose = () => {
    // Find the most recent activity date again
    const mostRecent = findMostRecentActivityDate();
    if (mostRecent) {
      setInternalSelectedDay(mostRecent);
      if (onDayClick) {
        onDayClick(mostRecent.dateStr, mostRecent.date);
      }
    } else {
      setInternalSelectedDay(null);
      if (onDayClick) {
        onDayClick(null, null);
      }
    }
  };

  return (
    <div className="modern-activity-section">
      {/* Main content area - Calendar with always-visible sidebar */}
      <div className="activity-calendar-view with-sidebar">
        <div className="sleek-calendar-container">
          {/* Date Range Selector */}
          <div className="calendar-date-range-selector">
            <button 
              className="calendar-date-nav-button"
              onClick={() => onWeekOffsetChange && onWeekOffsetChange(weekOffset + 1)}
              title="Go back 4 weeks"
            >
              <Icon name="ChevronLeft" className="calendar-date-nav-icon" />
            </button>
            <div className="calendar-date-range-text">{getDateRangeDisplay()}</div>
            <button 
              className="calendar-date-nav-button"
              onClick={() => onWeekOffsetChange && weekOffset > 0 && onWeekOffsetChange(weekOffset - 1)}
              title="Go forward 4 weeks"
              disabled={weekOffset <= 0}
            >
              <Icon name="ChevronRight" className="calendar-date-nav-icon" />
            </button>
          </div>
          
          <div className="sleek-calendar">
            <div className="sleek-calendar-day-labels">
              {dayAbbreviations.map((day, index) => (
                <div key={index} className="sleek-day-label">{day}</div>
              ))}
            </div>
            
            <div className="sleek-calendar-grid">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="sleek-week-row">
                  {week.map((date, dayIndex) => {
                    const dateStr = date.toISOString().slice(0, 10);
                    const biohackCount = getBiohackCountForDay(dateStr);
                    const isToday = dateStr === new Date().toISOString().slice(0, 10);
                    const isSelected = activeSelectedDay && dateStr === activeSelectedDay.dateStr;
                    
                    // Calculate shade intensity based on activity count
                    // Max shade at 10+ activities, scale from 0-10
                    const maxActivities = 10;
                    const intensity = Math.min(biohackCount / maxActivities, 1);
                    // Opacity ranges from 0.2 (light) to 0.9 (dark) for blue shade
                    const opacity = biohackCount > 0 ? 0.2 + (intensity * 0.7) : 0;
                    
                    return (
                      <div
                        key={dateStr}
                        className={`sleek-calendar-day ${biohackCount > 0 ? 'has-activity' : ''} ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}`}
                        title={`${date.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'short', 
                          day: 'numeric' 
                        })}: ${biohackCount} biohack${biohackCount !== 1 ? 's' : ''}`}
                        onClick={() => handleDayClick(dateStr, date)}
                        style={{
                          backgroundColor: opacity > 0 
                            ? `rgba(59, 130, 246, ${opacity})` // Blue shade with variable opacity
                            : undefined
                        }}
                      >
                        <div className="sleek-day-number-wrapper">
                          <div className="sleek-day-number">{date.getDate()}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          
          {/* Stats at bottom of calendar */}
          <div className="calendar-stats">
            <div className="calendar-stat">
              <span className="calendar-stat-label">Total Logs</span>
              <span className="calendar-stat-value">{totalActivities}</span>
            </div>
            
            <div className="calendar-stat">
              <span className="calendar-stat-label">Weekly Average</span>
              <span className="calendar-stat-value">{weeklyAverage}</span>
            </div>
            
            <div className="calendar-stat">
              <span className="calendar-stat-label">Most Active Day</span>
              <span className="calendar-stat-value">{mostActiveDay.day}</span>
            </div>
          </div>
        </div>
        
        {/* Sidebar - always visible */}
        <div className="calendar-sidebar" ref={sidebarRef}>
          {activeSelectedDay ? (
            selectedBiohack ? (
              // Biohack detail view
              <div className="calendar-sidebar-content">
                <button 
                  className="calendar-sidebar-back"
                  onClick={() => setSelectedBiohack(null)}
                  title="Back to biohack list"
                >
                  <Icon name="ChevronLeft" className="icon--sm" />
                  <span>Back</span>
                </button>
                
                <div className="calendar-sidebar-detail">
                  <div className="calendar-sidebar-detail-header">
                    <div 
                      className="calendar-sidebar-detail-image-wrapper"
                      onClick={() => {
                        if (onBiohackClick) {
                          onBiohackClick(selectedBiohack);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                      title="Click to view biohack details"
                    >
                      <img 
                        src={selectedBiohack.image_url || '/default-biohack.jpg'} 
                        alt={selectedBiohack.name}
                        className="calendar-sidebar-detail-image"
                        onError={(e) => {
                          e.target.src = '/default-biohack.jpg';
                        }}
                      />
                    </div>
                    <div className="calendar-sidebar-detail-title">{selectedBiohack.name}</div>
                  </div>
                  
                  <div className="calendar-sidebar-detail-content">
                    {/* Biohack Stats Section */}
                    <div className="calendar-sidebar-biohack-stats">
                      <div className="calendar-sidebar-detail-section-title">
                        Biohack Stats
                        <span className="calendar-sidebar-stats-badge">Since Inception</span>
                      </div>
                      {statsLoading ? (
                        <div className="calendar-sidebar-stats-loading">
                          <JellyfishLoader />
                        </div>
                      ) : (
                        <div className="calendar-sidebar-biohack-stats-grid">
                          <div className="calendar-sidebar-biohack-stat">
                            <span className="calendar-sidebar-biohack-stat-label">Total Logs</span>
                            <span className="calendar-sidebar-biohack-stat-value">{biohackStats.totalActivities}</span>
                          </div>
                          
                          <div className="calendar-sidebar-biohack-stat">
                            <span className="calendar-sidebar-biohack-stat-label">Weekly Average</span>
                            <span className="calendar-sidebar-biohack-stat-value">{biohackStats.weeklyAverage}</span>
                          </div>
                          
                          <div className="calendar-sidebar-biohack-stat">
                            <span className="calendar-sidebar-biohack-stat-label">Most Active Day</span>
                            <span className="calendar-sidebar-biohack-stat-value">{biohackStats.mostActiveDay}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="calendar-sidebar-detail-stats">
                      <div className="calendar-sidebar-detail-section-title">Logging Activity</div>
                      {chartLoading ? (
                        <div className="calendar-sidebar-chart-loading">
                          <JellyfishLoader />
                        </div>
                      ) : biohackChartData.weeks.length > 0 ? (
                        <div className="calendar-sidebar-chart-container">
                          <ReactECharts
                            option={{
                              backgroundColor: 'transparent',
                              tooltip: {
                                trigger: 'axis',
                                backgroundColor: getCSSVar('--color-surface-glass-light'),
                                borderColor: getCSSVar('--color-border-primary'),
                                borderWidth: 1,
                                textStyle: { color: getCSSVar('--color-text-primary'), fontSize: 13 },
                                axisPointer: {
                                  type: 'cross',
                                  label: { backgroundColor: getCSSVar('--color-primary') }
                                }
                              },
                              grid: {
                                left: '8%',
                                right: '8%',
                                bottom: '15%',
                                top: '10%',
                                containLabel: true
                              },
                              xAxis: {
                                type: 'category',
                                data: biohackChartData.weeks,
                                axisLabel: { 
                                  color: getCSSVar('--color-text-secondary'), 
                                  fontSize: 10,
                                  rotate: 45,
                                  interval: 0
                                },
                                axisLine: { lineStyle: { color: getCSSVar('--color-border-primary') } },
                              },
                              yAxis: {
                                type: 'value',
                                axisLabel: { 
                                  color: getCSSVar('--color-text-secondary'), 
                                  fontSize: 11,
                                  formatter: (value) => Math.floor(value).toString()
                                },
                                splitLine: { lineStyle: { color: getCSSVar('--color-border-secondary'), type: 'dashed' } },
                                axisLine: { lineStyle: { color: getCSSVar('--color-border-primary') } },
                                minInterval: 1
                              },
                              series: [
                                {
                                  name: 'Logs',
                                  type: 'line',
                                  data: biohackChartData.counts,
                                  smooth: true,
                                  symbol: 'none',
                                  itemStyle: { color: getCSSVar('--color-primary') },
                                  lineStyle: { width: 3 },
                                  areaStyle: {
                                    color: {
                                      type: 'linear',
                                      x: 0,
                                      y: 0,
                                      x2: 0,
                                      y2: 1,
                                      colorStops: [
                                        { offset: 0, color: getCSSVar('--color-info-bg') },
                                        { offset: 1, color: getCSSVar('--color-primary-light') }
                                      ]
                                    }
                                  }
                                }
                              ]
                            }}
                            style={{ height: '280px', width: '100%' }}
                          />
                        </div>
                      ) : (
                        <div className="calendar-sidebar-chart-empty">
                          <div className="calendar-sidebar-chart-empty-text">No logging data available</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Biohack list view
              <div className="calendar-sidebar-content">
                <div className="calendar-sidebar-header">
                  <div className="calendar-sidebar-title">
                    {activeSelectedDay.date.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                  <button 
                    className="calendar-sidebar-close"
                    onClick={handleSidebarClose}
                    title="Reset to most recent activity"
                  >
                    <Icon name="RotateCw" className="icon--sm" />
                  </button>
                </div>
                
                <div className="calendar-sidebar-subtitle">
                  {selectedDayBiohacks.length} biohack{selectedDayBiohacks.length !== 1 ? 's' : ''} logged
                </div>
                
                {selectedDayBiohacks.length > 0 ? (
                  <div className="calendar-sidebar-biohacks-list">
                    {selectedDayBiohacks.map((biohack, index) => {
                      // Each entry now has a single protocol name
                      const protocolName = biohack.protocolName;
                      
                      return (
                        <div
                          key={`${biohack.id}-${biohack.protocolId}-${index}`}
                          className="calendar-sidebar-biohack-item"
                          onClick={() => setSelectedBiohack(biohack)}
                        >
                          <div className="calendar-sidebar-biohack-content">
                            <div className="calendar-sidebar-biohack-name">{biohack.name}</div>
                            {protocolName && (
                              <div className="calendar-sidebar-biohack-protocol-tags">
                                <span className="calendar-sidebar-biohack-protocol-tag">
                                  {protocolName}
                                </span>
                              </div>
                            )}
                          </div>
                          <Icon name="ChevronRight" className="icon--sm calendar-sidebar-biohack-arrow" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="calendar-sidebar-empty">
                    <div className="calendar-sidebar-empty-text">No biohacks logged on this day</div>
                  </div>
                )}
              </div>
            )
          ) : (
            // No activity state
            <div className="calendar-sidebar-content">
              <div className="calendar-sidebar-header">
                <div className="calendar-sidebar-title">No Activity</div>
              </div>
              <div className="calendar-sidebar-empty">
                <div className="calendar-sidebar-empty-text">
                  {isOwnProfile ? 'No activity logged yet' : 'This user has no activity logged yet'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const MOBILE_BREAKPOINT = 480;

function FollowersModal({ open, onClose, list, title, loading = false }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Click outside to close functionality (desktop only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && !isMobile && !event.target.closest('.followers-modal-dropdown') && !event.target.closest('.followers-modal-standard-overlay')) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose, isMobile]);

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  if (!open) return null;

  const handleUserClick = (userId) => {
    onClose();
    navigate(`/user/${userId}`);
  };

  const filteredList = searchQuery
    ? list.filter(user =>
        (user.username || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : list;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="followers-modal-content">
          <div className="followers-modal-loading">
            <JellyfishLoader />
          </div>
        </div>
      );
    }

    if (filteredList.length === 0) {
      return (
        <div className="followers-modal-content">
          <div className="followers-modal-empty">
            {searchQuery ? `No ${title.toLowerCase()} found matching "${searchQuery}"` : `No ${title.toLowerCase()} yet`}
          </div>
        </div>
      );
    }

    return (
      <div className="followers-modal-content">
        <div className="followers-modal-list">
          {filteredList.map(user => (
            <div
              key={user.id}
              className="followers-modal-item"
              onClick={() => handleUserClick(user.id)}
            >
              <div className="followers-modal-avatar">
                {user.avatar || user.profile_picture_url ? (
                  <img
                    src={user.avatar || user.profile_picture_url}
                    alt="avatar"
                    className="followers-modal-avatar-img"
                  />
                ) : (
                  <span className="followers-modal-avatar-fallback">
                    {(user.username || 'U').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="followers-modal-username">{user.username}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Mobile: standard centered modal (same design as disclaimer modal)
  if (isMobile) {
    return (
      <div
        className="followers-modal-standard-overlay"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="followers-modal-standard-box" onClick={e => e.stopPropagation()}>
          <div className="followers-modal-standard-header">
            <h3 className="followers-modal-standard-title">{title}</h3>
            <button
              type="button"
              className="followers-modal-standard-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="followers-modal-standard-body">
            <div className="followers-modal-search-container">
              <div className="followers-modal-search-input-wrapper">
                <Icon name="Search" className="followers-modal-search-icon" />
                <input
                  type="text"
                  className="followers-modal-search-input"
                  placeholder={`Search ${title.toLowerCase()}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            {renderContent()}
          </div>
        </div>
      </div>
    );
  }

  // Desktop: sliding panel
  return (
    <div className="followers-modal-backdrop">
      <div className="followers-modal-dropdown" onClick={e => e.stopPropagation()}>
        <div className="followers-modal-header">
          <div className="followers-modal-title">{title}</div>
          <button
            className="followers-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="followers-modal-search-container">
          <div className="followers-modal-search-input-wrapper">
            <Icon name="Search" className="followers-modal-search-icon" />
            <input
              type="text"
              className="followers-modal-search-input"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {renderContent()}
      </div>
    </div>
  );
}

const UserPage = ({ userId }) => {
  const [userData, setUserData] = useState(null);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [protocols, setProtocols] = useState([]);
  const [protocolsLoading, setProtocolsLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [publicProfile, setPublicProfile] = useState(true);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [biohacks, setBiohacks] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [currentUser, setCurrentUser] = useState(null); // Full user object from auth
  const [currentUserId, setCurrentUserId] = useState(null); // Derived from currentUser
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowPending, setIsFollowPending] = useState(false); // Track if follow is pending approval
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [optingInId, setOptingInId] = useState(null);
  const [forumPosts, setForumPosts] = useState([]);
  const [forumPostsLoading, setForumPostsLoading] = useState(true);
  const [forumPostsError, setForumPostsError] = useState(null);
  const [scheduleSlideOverOpen, setScheduleSlideOverOpen] = useState(false);
  const [notesSlideOverOpen, setNotesSlideOverOpen] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [selectedProtocolForNotes, setSelectedProtocolForNotes] = useState(null);
  const [protocolSchedules, setProtocolSchedules] = useState({}); // { protocolId: { hackId: { frequency, times } } }
  const [forumPostsLikesInfo, setForumPostsLikesInfo] = useState({}); // Store likes/comments info for forum posts
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [showAllBiohacks, setShowAllBiohacks] = useState(false);
  const [activityWeekOffset, setActivityWeekOffset] = useState(0); // 0 = current 4 weeks, 1 = previous 4 weeks, etc.
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  
  // Use centralized onboarding hook
  const [showOnboarding, setShowOnboarding] = useOnboarding({
    shouldCheck: !loading && !!currentUserId,
    userId: currentUserId
  });
  
  // Prevent duplicate concurrent protocol fetches
  const fetchProtocolsInProgressRef = useRef(false);
  // Prevent duplicate concurrent user data fetches
  const fetchUserAndDataInProgressRef = useRef(false);
  
  // Use centralized protocol actions hook
  const {
    userVotes,
    votingProtocolId,
    likedProtocols,
    likingProtocolId,
    handleVote: handleVoteAction,
    handleToggleLike: handleToggleLikeAction,
    fetchUserVotes,
    fetchLikedProtocols
  } = useProtocolActions();
  const { hasProtocol, refresh: refreshProtocolStatuses } = useUserProtocolStatus();
  const [copyingProtocolId, setCopyingProtocolId] = useState(null);
  const [allUserProtocols, setAllUserProtocols] = useState([]);
  const [profilePictureUploaded, setProfilePictureUploaded] = useState(false);
  const [selectedBiohack, setSelectedBiohack] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [profileSettings, setProfileSettings] = useState({
    email: '',
    username: '',
    country: ''
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deletingProfilePicture, setDeletingProfilePicture] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [savingBio, setSavingBio] = useState(false);
  const [showProtocolUsers, setShowProtocolUsers] = useState(false);
  const [selectedProtocolUsers, setSelectedProtocolUsers] = useState([]);
  const [protocolUsersLoading, setProtocolUsersLoading] = useState(false);
  const [tabPrivacySettings, setTabPrivacySettings] = useState({});
  // null = not yet computed (don't fetch sensitive data); [] = no access; [...] = allowed tabs
  const [visibleTabs, setVisibleTabs] = useState(null);
  const [protocolsFilter, setProtocolsFilter] = useState('active'); // 'active' or 'staged'
  const [selectedBiotags, setSelectedBiotags] = useState([]); // array of biohack ids
  const [showBiotagFilter, setShowBiotagFilter] = useState(false);
  const biotagFilterRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // State for selected day details
  const [selectedDay, setSelectedDay] = useState(null);

  // State for deactivation confirmation modal
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [pendingDeactivateProtocol, setPendingDeactivateProtocol] = useState(null);

  // Protocol activation function
  const handleProtocolActivation = async (protocolId) => {
    try {
      // Find the protocol to get its status record ID
      const protocol = protocols.find(p => p.id === protocolId);
      if (!protocol) {
        throw new Error('Protocol not found');
      }

      const isCurrentlyActive = protocol.status === 'active';
      const newStatus = !isCurrentlyActive;

      // Update the protocols_status table
      const updateData = {
        is_active: newStatus,
        ...(newStatus 
          ? { activated_at: new Date().toISOString(), deactivated_at: null }
          : { deactivated_at: new Date().toISOString() }
        )
      };

      const { error } = await supabase
        .from('protocols_status')
        .update(updateData)
        .eq('id', protocol.user_protocol_status_id);

      if (error) {
        throw error;
      }

      // If deactivating, also set logging_schedules.is_active to false for this protocol
      if (!newStatus) {
        const { error: scheduleError } = await supabase
          .from('logging_schedules')
          .update({ is_active: false })
          .eq('user_id', currentUserId)
          .eq('protocol_id', protocolId)
          .eq('is_active', true);

        if (scheduleError) {
          console.error('Failed to deactivate logging schedules:', sanitizeErrorForLog(scheduleError));
          // Don't throw - protocol deactivation still succeeded
        }
      }

      // Update local state for both protocols and allUserProtocols
      const updateProtocolStatus = (p) => {
        if (p.id === protocolId) {
          return {
            ...p,
            status: newStatus ? 'active' : 'staged',
            activated_at: newStatus ? new Date().toISOString() : p.activated_at,
            deactivated_at: newStatus ? null : new Date().toISOString()
          };
        }
        return p;
      };
      
      setProtocols(prevProtocols => prevProtocols.map(updateProtocolStatus));
      setAllUserProtocols(prevProtocols => prevProtocols.map(updateProtocolStatus));
    } catch (error) {
      // Error handled by UI state
    }
  };

  // Handler for activating a protocol (for staged protocols)
  const handleActivateProtocol = async (protocol) => {
    if (!protocol?.id) return;
    await handleProtocolActivation(protocol.id);
  };

  // Handler for deactivating a protocol (for active protocols)
  const handleDeactivateProtocol = async (protocol) => {
    if (!protocol?.id) return;
    // Show confirmation modal instead of directly deactivating
    setPendingDeactivateProtocol(protocol);
    setShowDeactivateModal(true);
  };

  // Handler for confirming deactivation
  const handleConfirmDeactivate = async () => {
    if (!pendingDeactivateProtocol?.id) {
      setShowDeactivateModal(false);
      setPendingDeactivateProtocol(null);
      return;
    }
    setShowDeactivateModal(false);
    const protocolToDeactivate = pendingDeactivateProtocol;
    setPendingDeactivateProtocol(null);
    await handleProtocolActivation(protocolToDeactivate.id);
  };

  // Handler for canceling deactivation
  const handleCancelDeactivate = () => {
    setShowDeactivateModal(false);
    setPendingDeactivateProtocol(null);
  };

  // Handler for delete protocol - deletes immediately
  const handleDeleteProtocol = async (protocol) => {
    if (!protocol?.id) return;

    try {
      const protocolId = protocol.id;

      // Check if user is the creator - if so, delete the entire protocol
      if (protocol.created_by === currentUserId) {
        // Delete the protocol - related records should cascade delete:
        // - protocol_hacks: CASCADE DELETE (from fix-protocol-hacks-fk-index.sql)
        // - protocols_status: CASCADE DELETE (from fix-protocols-status-fk-index.sql)
        // - logging_hack_entries: CASCADE DELETE (from fix-logging-hack-entries-fk-index.sql)
        // - logging_schedules: Should have CASCADE DELETE
        // - protocol_report_snapshots: Should have CASCADE DELETE
        // - protocol_ratings: Should have CASCADE DELETE after running fix-protocol-deletion-cascade.sql
        const { error: protocolError } = await supabase
          .from('protocols')
          .delete()
          .eq('id', protocolId)
          .eq('created_by', currentUserId);

        if (protocolError) {
          // Check if it's the foreign key constraint error
          if (protocolError.message.includes('foreign key constraint') && 
              protocolError.message.includes('protocol_ratings')) {
            throw new Error('Cannot delete protocol: Database constraint issue. Please run the fix-protocol-deletion-cascade.sql migration to enable cascade deletion of ratings.');
          }
          throw protocolError;
        }

        // Remove the protocol from local state
        setProtocols(prevProtocols => prevProtocols.filter(p => p.id !== protocolId));
        setAllUserProtocols(prevProtocols => prevProtocols.filter(p => p.id !== protocolId));
      } 
      // If user added this protocol to their profile (has user_protocol_status_id) but didn't create it
      // Just remove it from their profile by deleting the protocols_status record
      else if (protocol.user_protocol_status_id) {
        const { error: statusError } = await supabase
          .from('protocols_status')
          .delete()
          .eq('id', protocol.user_protocol_status_id)
          .eq('user_id', currentUserId);

        if (statusError) {
          throw statusError;
        }

        // Remove the protocol from local state
        setProtocols(prevProtocols => prevProtocols.filter(p => p.id !== protocolId));
        setAllUserProtocols(prevProtocols => prevProtocols.filter(p => p.id !== protocolId));
      } else {
        alert('You can only delete protocols you created or added to your profile.');
        return;
      }
    } catch (error) {
      console.error('Error deleting protocol:', sanitizeErrorForLog(error));
      alert(getUserFacingErrorMessage(error, 'Failed to delete protocol. Please try again.'));
    }
  };

  // Helper: Check if protocol exists for user by protocol_id, return true if exists
  const checkExistingProtocol = async (userId, protocolId) => {
    const { data, error } = await supabase
      .from('protocols_status')
      .select('id')
      .eq('user_id', userId)
      .eq('protocol_id', protocolId)
      .single();
    
    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      throw error;
    }
    
    const result = data !== null;
    return result;
  };

  const getProtocolHacksArray = (protocol) => {
    // Prefer protocol_hacks if present, else use hacks array
    if (protocol.protocol_hacks && protocol.protocol_hacks.length > 0) {
      const result = protocol.protocol_hacks.map((hack, index) => ({
        hack_id: hack.hack_id,
        position: hack.position ?? index
      }));
      return result;
    } else if (protocol.hacks && protocol.hacks.length > 0) {
      const result = protocol.hacks.map((hack, index) => ({
        hack_id: hack.id,
        position: index
      }));
      return result;
    }
    return [];
  };

  // Protocol copy function - exactly the same as ProtocolCatalogue's handleAddProtocol
  const handleProtocolCopy = async (protocol) => {
    try {
      setCopyingProtocolId(protocol.id);
      
      const user = currentUser || await getCurrentUser();
      
      if (!user) {
        setSuccessMessage('Please sign in to add protocols');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        return;
      }
      
      if (!protocol) {
        setSuccessMessage('Please select a protocol to add');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        return;
      }
      
      const protocolExists = await checkExistingProtocol(user.id, protocol.id);
      
      if (protocolExists) {
        setSuccessMessage('You already have this protocol in your profile');
        setShowSuccessMessage('warning'); // Use 'warning' instead of true
        setTimeout(() => setShowSuccessMessage(false), 3000);
        return;
      }
      
      const protocolHacksArr = getProtocolHacksArray(protocol);
      
      if (!protocolHacksArr.length) {
        setSuccessMessage('Cannot add a protocol with no hacks.');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        return;
      }
      
      // Create protocol status record for the user
      const { data: protocolStatus, error: statusError } = await supabase
        .from('protocols_status')
        .insert({
          user_id: user.id,
          protocol_id: protocol.id,
          is_active: false,
          added_at: new Date().toISOString()
        })
        .select()
        .single();

      if (statusError) {
        throw statusError;
      }
      
      // Protocol hacks are already associated with the original protocol, no need to copy
      setSuccessMessage('Protocol added!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      
      // Refresh the protocols list to show the newly added protocol
      await fetchUserAndData();
    } catch (error) {
      setSuccessMessage('Error adding protocol. Please try again.');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } finally {
      setCopyingProtocolId(null);
    }
  };

  // Handle showing protocol users
  const handleShowProtocolUsers = async (protocol) => {
    setProtocolUsersLoading(true);
    try {
      // First, get the user_ids from protocols_status
      const { data: protocolStatuses, error: statusError } = await supabase
        .from('protocols_status')
        .select('user_id')
        .eq('protocol_id', protocol.id);

      if (statusError) throw statusError;

      if (!protocolStatuses || protocolStatuses.length === 0) {
        setSelectedProtocolUsers([]);
        setShowProtocolUsers(true);
        return;
      }

      // Extract user_ids
      const userIds = protocolStatuses.map(status => status.user_id).filter(Boolean);

      if (userIds.length === 0) {
        setSelectedProtocolUsers([]);
        setShowProtocolUsers(true);
        return;
      }

      // Fetch user details and profile pictures from user_profiles table with deduplication
      const profilesCacheKey = getCacheKey('fetchUserProfiles', userIds.sort().join(','));
      const profilesData = await dedupeRequest(profilesCacheKey, async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id, username, profile_picture_url')
          .in('user_id', userIds);
        if (error) throw error;
        return data;
      });

      // Create maps for easier lookup
      const usersMap = {};
      const profileMap = {};
      if (profilesData) {
        profilesData.forEach(profile => {
          usersMap[profile.user_id] = {
            id: profile.user_id,
            username: profile.username
          };
          profileMap[profile.user_id] = profile.profile_picture_url;
        });
      }
      
      const usersData = Object.values(usersMap);

      // Format the users data
      const users = (usersData || []).map(user => ({
        id: user.id,
        username: user.username,
        avatar: profileMap[user.id] || null
      }));

      setSelectedProtocolUsers(users);
      setShowProtocolUsers(true);
    } catch (error) {
      alert('Failed to load protocol users');
    } finally {
      setProtocolUsersLoading(false);
    }
  };

  // Handle day click to show detailed activities
  const handleDayClick = (dateStr, date) => {
    const dayActivities = activity.filter(act => act.date === dateStr);
    
    if (dayActivities.length > 0) {
      setSelectedDay({
        date: date,
        dateStr: dateStr,
        activities: dayActivities
      });
    } else {
      setSelectedDay(null);
    }
  };

  // Fetch tab privacy settings for the viewed user
  const fetchTabPrivacySettings = useCallback(async (targetUserId) => {
    try {
      const cacheKey = getCacheKey('fetchTabPrivacySettings', targetUserId);
      const data = await dedupeRequest(cacheKey, async () => {
        const { data, error } = await supabase
          .from('user_tab_privacy')
          .select('tab_name, privacy_level')
          .eq('user_id', targetUserId);

        if (error) throw error;
        return data;
      });

      // Convert array to object for easier access
      const settings = {};
      if (data) {
        data.forEach(item => {
          settings[item.tab_name] = item.privacy_level;
        });
      }

      // Set default 'followers_only' for tabs without settings
      const allTabs = ['activity', 'protocols', 'posts', 'biomarkers', 'challenges', 'wearables', 'logging'];
      allTabs.forEach(tab => {
        if (!settings[tab]) {
          settings[tab] = 'followers_only';
        }
      });

      setTabPrivacySettings(settings);
      return settings;
    } catch (error) {
      console.error('Error fetching tab privacy settings:', sanitizeErrorForLog(error));
      return {};
    }
  }, []);

  // Check which tabs the current user can view
  const checkVisibleTabs = useCallback(async (targetUserId, viewerId, settings) => {
    // Owner can see all tabs
    if (targetUserId === viewerId) {
      setVisibleTabs(['activity', 'protocols', 'posts', 'biomarkers', 'challenges', 'wearables', 'logging']);
      return;
    }

    // Check if viewer is following the target user with deduplication
    // Only count accepted follows (accepted_at IS NOT NULL)
    let isFollowingUser = false;
    if (viewerId) {
      const checkVisibleTabsFollowsCacheKey = getCacheKey('checkVisibleTabsFollows', `${viewerId}_${targetUserId}`);
      const data = await dedupeRequest(checkVisibleTabsFollowsCacheKey, async () => {
        const { data, error } = await supabase
          .from('follows')
          .select('id')
          .eq('follower_id', viewerId)
          .eq('followee_id', targetUserId)
          .not('accepted_at', 'is', null)  // Only count accepted follows
          .maybeSingle();
        if (error) throw error;
        return data;
      });

      isFollowingUser = data !== null;
    }

    // Determine which tabs are visible
    const visible = [];
    const allTabs = ['activity', 'protocols', 'posts', 'biomarkers', 'challenges', 'wearables', 'logging'];
    
    allTabs.forEach(tab => {
      const privacyLevel = settings[tab] || 'followers_only';
      
      if (privacyLevel === 'public') {
        visible.push(tab);
      } else if (privacyLevel === 'followers_only' && isFollowingUser) {
        visible.push(tab);
      }
      // private tabs are not added to visible array
    });

    setVisibleTabs(visible);
  }, []);

  // Handle tab privacy change callback
  const handleTabPrivacyChange = useCallback((tabName, newPrivacyLevel) => {
    setTabPrivacySettings(prev => ({
      ...prev,
      [tabName]: newPrivacyLevel
    }));
  }, []);

  // Cached getUser function with deduplication (5s keepAlive so Strict Mode remount / delayed effects don't re-fetch)
  const getCurrentUser = useCallback(async () => {
    const cacheKey = getCacheKey('getCurrentUser', []);
    return dedupeRequest(
      cacheKey,
      async () => {
        const { data: { user } = {}, error } = await supabase.auth.getUser();
        if (error) {
          console.error('Error fetching current user:', sanitizeErrorForLog(error));
          return null;
        }
        return user;
      },
      { keepAliveMs: 5000 }
    );
  }, []);

  useEffect(() => {
    // Fetch current user once on mount
    const fetchCurrentUser = async () => {
      const user = await getCurrentUser();
      setCurrentUser(user);
      setCurrentUserId(user?.id || null);
    };
    fetchCurrentUser();
  }, [getCurrentUser]);

  // Set dummy data for testing
  useEffect(() => {
    setProtocols(protocols); // Use dummy protocols
    // Removed dummy activity - using real data from database
  }, []);

  const fetchUserAndData = useCallback(async () => {
    // Prevent duplicate concurrent requests
    if (fetchUserAndDataInProgressRef.current) {
      console.warn('fetchUserAndData: Request already in progress, skipping duplicate');
      return;
    }
    
    fetchUserAndDataInProgressRef.current = true;
    setLoading(true);

    try {
      let id = userId;
      if (!id) {
        // userId is undefined when viewing own profile via /user route
        const user = currentUser || await getCurrentUser();
        if (!user) {
          setUserData(null);
          setUsername('');
          setActivity([]);
          fetchUserAndDataInProgressRef.current = false;
          return;
        }
        id = user.id;
      }
      
      // Fetch user profile with deduplication - use unique cache key to avoid conflicts with TopNavigation
      // Select only safe, public columns to avoid exposing email, admin_flag, last_login, dob to other users
      const me = currentUser || await getCurrentUser();
      const isOwnProfileFetch = me && id === me.id;
      const profileColumns = 'user_id, username, profile_picture_url, bio, followers_count, following_count, user_level, xp, country' + (isOwnProfileFetch ? ', email' : '');
      let profileData;
      let profileError = null;
      try {
        const profileCacheKey = getCacheKey('fetchUserProfileForPage', id);
        profileData = await dedupeRequest(profileCacheKey, async () => {
          const { data, error } = await supabase
            .from('user_profiles')
            .select(profileColumns)
            .eq('user_id', id)
            .maybeSingle();
          if (error) throw error;
          return data;
        }, { caller: 'UserPage' });
      } catch (error) {
        profileError = error;
      }
      
      if (profileError) {
        console.error('Error fetching user profile:', { userId: id, ...sanitizeErrorForLog(profileError) });
        // Don't set userData if query failed - this will show loading state
        setUserData(null);
        setUsername('');
        setBioText('');
        setFollowersCount(0);
      } else if (profileData) {
        setUserData(profileData);
        setFollowersCount(profileData?.followers_count ?? 0);
        setBioText(profileData?.bio || '');
        // Username is already in profileData
        setUsername(profileData?.username || '');
      } else {
        // No data returned (shouldn't happen with .single() but handle it)
        console.warn('No profile data returned for user:', id);
        setUserData(null);
        setUsername('');
        setBioText('');
        setFollowersCount(0);
      }
      // Activity (logging_hack_entries) is fetched only when viewer has access - see fetchActivity effect
    } catch (error) {
      console.error('Error fetching user data:', sanitizeErrorForLog(error));
    } finally {
      setLoading(false);
      fetchUserAndDataInProgressRef.current = false;
    }
  }, [userId]); // Removed getCurrentUser from deps since it's stable (empty deps in useCallback)

  // Reset state when userId changes to prevent showing wrong user's data
  useEffect(() => {
    if (userId !== undefined) {
      setUserData(null);
      setUsername('');
      setActivity([]);
      setProtocols([]);
      setVisibleTabs(null);
      // Reset fetch refs to allow new fetches when userId changes
      fetchUserAndDataInProgressRef.current = false;
      fetchProtocolsInProgressRef.current = false;
    }
  }, [userId]);

  useEffect(() => {
    fetchUserAndData();
  }, [fetchUserAndData]);

  // Real-time updates for forum posts
  useEffect(() => {
    if (!forumPosts.length) return;
    
    const subscription = supabase
      .channel('user_forum_posts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts' }, (payload) => {
        // Only update if the changed post is in our forum posts
        const postIndex = forumPosts.findIndex(p => p.id === payload.new.id);
        if (postIndex !== -1) {
          setForumPosts(prev => prev.map((post, index) =>
            index === postIndex
              ? { ...post, likes_count: payload.new.likes_count, comments_count: payload.new.comments_count }
              : post
          ));
        }
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [forumPosts]);

  // Fetch tab privacy settings when user data is loaded
  useEffect(() => {
    const fetchAndCheckTabPrivacy = async () => {
      if (!userData) return;

      const targetUserId = userData.user_id || userData.id;
      if (!targetUserId) return;

      // Not logged in: no access to another user's tab content
      if (currentUserId === null) {
        setVisibleTabs([]);
        return;
      }

      const settings = await fetchTabPrivacySettings(targetUserId);
      await checkVisibleTabs(targetUserId, currentUserId, settings);
    };

    fetchAndCheckTabPrivacy();
    // Only depend on the user ID, not the entire userData object to prevent unnecessary re-runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user_id || userData?.id, currentUserId, fetchTabPrivacySettings, checkVisibleTabs]);

  useEffect(() => {
    const handleActivityLogged = () => {
      fetchUserAndData();
    };
    window.addEventListener('activity-logged', handleActivityLogged);
    return () => window.removeEventListener('activity-logged', handleActivityLogged);
  }, [fetchUserAndData]);

  const checkFollowing = useCallback(async () => {
    if (!currentUserId || !userId || currentUserId === userId) {
      setIsFollowing(false);
      setIsFollowPending(false);
      return;
    }
    const checkFollowingCacheKey = getCacheKey('checkFollowing', `${currentUserId}_${userId}`);
    const data = await dedupeRequest(checkFollowingCacheKey, async () => {
      const { data, error } = await supabase
        .from('follows')
        .select('follower_id, followee_id, accepted_at')
        .eq('follower_id', currentUserId)
        .eq('followee_id', userId)
        .maybeSingle();
      if (error) throw error;
      return data;
    });
    
    if (data) {
      setIsFollowing(true);
      setIsFollowPending(data.accepted_at === null); // Pending if accepted_at is null
    } else {
      setIsFollowing(false);
      setIsFollowPending(false);
    }
  }, [currentUserId, userId]);

  useEffect(() => {
    checkFollowing();
  }, [checkFollowing]);

  // Fetch activity (logging_hack_entries) only when viewer is allowed to see it
  const fetchActivity = useCallback(async (profileUserId) => {
    if (!profileUserId) return;
    setActivityLoading(true);
    try {
      const loggingCacheKey = getCacheKey('fetchLoggingHackEntries', profileUserId);
      const loggingData = await dedupeRequest(loggingCacheKey, async () => {
        const { data, error } = await supabase
          .from('logging_hack_entries')
          .select('date, protocol_id, hack_id')
          .eq('user_id', profileUserId);
        if (error) throw error;
        return data;
      });
      const activityMap = {};
      (loggingData || []).forEach(entry => {
        if (!entry.date || !entry.protocol_id || !entry.hack_id) return;
        const dateStr = entry.date;
        if (!activityMap[dateStr]) activityMap[dateStr] = [];
        activityMap[dateStr].push({ protocol_id: entry.protocol_id, hack_id: entry.hack_id });
      });
      const activityArr = Object.entries(activityMap).map(([date, protocols]) => ({ date, protocols }));
      setActivity(activityArr);
    } catch (error) {
      console.error('Error fetching activity:', sanitizeErrorForLog(error));
      setActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const isOwnProfile = !userId || currentUserId === userId;
  const canFetchSensitiveData = visibleTabs !== null && (isOwnProfile || (visibleTabs && visibleTabs.length > 0));

  // Only fetch activity when viewer has access (avoids leaking data in network tab)
  useEffect(() => {
    if (!canFetchSensitiveData || !userData) return;
    const profileUserId = userData.user_id || userData.id;
    if (!profileUserId) return;
    fetchActivity(profileUserId);
  }, [canFetchSensitiveData, userData?.user_id, userData?.id, fetchActivity]);

  const fetchFollowers = useCallback(async () => {
    const targetUserId = userId || currentUserId;
    
    // Guard clause: don't fetch if we don't have a valid user ID
    if (!targetUserId || targetUserId === 'undefined') {
      setFollowers([]);
      setFollowersLoading(false);
      return;
    }
    
    setFollowersLoading(true);
    try {
      // First, let's get the basic follows data with deduplication
      const followersFollowsCacheKey = getCacheKey('fetchFollowersFollows', targetUserId);
      const followsData = await dedupeRequest(followersFollowsCacheKey, async () => {
        const { data, error } = await supabase
          .from('follows')
          .select('follower_id')
          .eq('followee_id', targetUserId)
          .not('accepted_at', 'is', null);  // Only count accepted follows
        if (error) throw error;
        return data;
      });
      
      if (!followsData || followsData.length === 0) {
        setFollowers([]);
        return;
      }
      
      // Now get the user details for each follower
      // Filter out null follower_ids (these are incomplete follow relationships)
      const followerIds = followsData
        .map(follow => follow.follower_id)
        .filter(id => id !== null && id !== 'null');
      
      if (followerIds.length === 0) {
        setFollowers([]);
        return;
      }
      
      const followersCacheKey = getCacheKey('fetchFollowersProfiles', followerIds.sort().join(','));
      const usersData = await dedupeRequest(followersCacheKey, async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id, username, profile_picture_url')
          .in('user_id', followerIds);
        if (error) throw error;
        return data;
      });
      
      const followersList = (usersData || []).map(profile => ({
        id: profile.user_id,
        username: profile.username,
        avatar: profile.profile_picture_url
      }));
      
      setFollowers(followersList);
    } catch (error) {
      console.error('Error fetching followers:', sanitizeErrorForLog(error));
      setFollowers([]);
    } finally {
      setFollowersLoading(false);
    }
  }, [userId, currentUserId]);

  const fetchFollowing = useCallback(async () => {
    const targetUserId = userId || currentUserId;
    
    // Guard clause: don't fetch if we don't have a valid user ID
    if (!targetUserId || targetUserId === 'undefined') {
      setFollowing([]);
      setFollowingLoading(false);
      return;
    }
    
    setFollowingLoading(true);
    try {
      // First, let's get the basic follows data with deduplication
      const followingFollowsCacheKey = getCacheKey('fetchFollowingFollows', targetUserId);
      const followsData = await dedupeRequest(followingFollowsCacheKey, async () => {
        const { data, error } = await supabase
          .from('follows')
          .select('followee_id')
          .eq('follower_id', targetUserId)
          .not('accepted_at', 'is', null);  // Only count accepted follows
        if (error) throw error;
        return data;
      });
      
      if (!followsData || followsData.length === 0) {
        setFollowing([]);
        return;
      }
      
      // Now get the user details for each followee
      // Filter out null followee_ids (these are incomplete follow relationships)
      const followeeIds = followsData
        .map(follow => follow.followee_id)
        .filter(id => id !== null && id !== 'null');
      
      if (followeeIds.length === 0) {
        setFollowing([]);
        return;
      }
      
      const followingCacheKey = getCacheKey('fetchFollowingProfiles', followeeIds.sort().join(','));
      const usersData = await dedupeRequest(followingCacheKey, async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id, username, profile_picture_url')
          .in('user_id', followeeIds);
        if (error) throw error;
        return data;
      });
      
      const followingList = (usersData || []).map(profile => ({
        id: profile.user_id,
        username: profile.username,
        avatar: profile.profile_picture_url
      }));
      
      setFollowing(followingList);
    } catch (error) {
      console.error('Error fetching following:', sanitizeErrorForLog(error));
      setFollowing([]);
    } finally {
      setFollowingLoading(false);
    }
  }, [userId, currentUserId]);

  const handleFollowToggle = async () => {
    setFollowLoading(true);
    if (!isFollowing) {
      setIsFollowing(true); // Optimistic update
      setFollowersCount(c => c + 1); // Optimistic update
      
      const { data, error } = await supabase
        .from('follows')
        .insert({ follower_id: currentUserId, followee_id: userId })
        .select('accepted_at')
        .single();
      
      if (error) {
        setIsFollowing(false);
        setIsFollowPending(false);
        setFollowersCount(c => Math.max(0, c - 1));
        alert(getUserFacingErrorMessage(error, 'Could not follow user. Please try again.'));
        console.error('Follow error:', sanitizeErrorForLog(error));
      } else {
        // Check if follow is pending (accepted_at is null)
        setIsFollowPending(data?.accepted_at === null);
        // Notification is automatically created by database trigger create_follow_notification_trigger
        // No need to call NotificationService.notifyFollow() here
      }
    } else {
      setIsFollowing(false); // Optimistic update
      setIsFollowPending(false);
      setFollowersCount(c => Math.max(0, c - 1)); // Optimistic update
      const { error } = await supabase.from('follows')
        .delete()
        .eq('follower_id', currentUserId)
        .eq('followee_id', userId);
      if (error) {
        setIsFollowing(true);
        setFollowersCount(c => c + 1);
        alert(getUserFacingErrorMessage(error, 'Could not unfollow user. Please try again.'));
        console.error('Unfollow error:', sanitizeErrorForLog(error));
      }
    }
    setFollowLoading(false);
    // Re-check after a short delay to ensure DB is up to date
    setTimeout(() => {
      checkFollowing();
      // Refetch profile data to sync followersCount
      (async () => {
        let id = userId;
        if (!id) {
          const user = currentUser || await getCurrentUser();
          if (!user) return;
          id = user.id;
        }
        const followersCountCacheKey = getCacheKey('fetchFollowersCount', id);
        const profileData = await dedupeRequest(followersCountCacheKey, async () => {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('followers_count')
            .eq('user_id', id)
            .maybeSingle();
          if (error) throw error;
          return data;
        });
        setFollowersCount(profileData?.followers_count ?? 0);
      })();
    }, 200);
  };

  const handleMessage = () => {
    navigate('/messages', { state: { newMessageUserId: userId, newMessageUsername: username } });
  };

  const handleProfileSettingsSave = async (updatedSettings) => {
    try {
      const user = currentUser || await getCurrentUser();
      if (!user) {
        alert('Please sign in to update your profile');
        return;
      }

      // Update user_profiles table (including username)
      const { error } = await supabase
        .from('user_profiles')
        .update({
          username: updatedSettings.username,
          country: updatedSettings.country,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) {
        console.error('Error updating profile:', sanitizeErrorForLog(error));
        alert('Error updating profile. Please try again.');
        return;
      }

      // Update auth user metadata for username (optional, for consistency)
      const { error: authError } = await supabase.auth.updateUser({
        data: { username: updatedSettings.username }
      });

      if (authError) {
        console.error('Error updating username in auth:', sanitizeErrorForLog(authError));
        // Don't fail the whole operation if auth metadata update fails
        console.warn('Username updated in database but auth metadata update failed');
      }

      // Refresh user data and update cached user
      await fetchUserAndData();
      const updatedUser = await getCurrentUser();
      setCurrentUser(updatedUser);
      setCurrentUserId(updatedUser?.id || null);
      
      // Close modal
      setShowProfileSettings(false);
      
      // Show success message
      setSuccessMessage('Profile updated successfully!');
      setShowSuccessMessage(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving profile settings:', sanitizeErrorForLog(error));
      alert('Error updating profile. Please try again.');
    }
  };

  useEffect(() => {
    // Note: Biohacks are now fetched after protocols are loaded,
    // so we only fetch the biohacks actually used in the user's protocols
    // This is done in fetchProtocols to avoid fetching all 86+ biohacks
    fetchLikedProtocols();
    fetchUserVotes();
  }, []);

  useEffect(() => {
    // Don't fetch protocols (or biohacks) until we know the viewer has access
    if (!canFetchSensitiveData) {
      if (visibleTabs !== null) {
        setProtocols([]);
        setAllUserProtocols([]);
        setBiohacks([]);
        setProtocolsLoading(false);
      }
      return;
    }

    const fetchProtocols = async () => {
      // Prevent duplicate concurrent requests
      if (fetchProtocolsInProgressRef.current) {
        console.warn('fetchProtocols: Request already in progress, skipping duplicate');
        return;
      }

      fetchProtocolsInProgressRef.current = true;
      setProtocolsLoading(true);
      try {
        let id = userId;
        if (!id) {
          // userId is undefined when viewing own profile via /user route
          const user = currentUser || await getCurrentUser();
          if (!user) {
            setProtocols([]);
            setAllUserProtocols([]);
            return;
          }
          id = user.id;
        }
        
        // Check if we're viewing another user's profile
        const viewerUser = currentUser || await getCurrentUser();
        const isViewingOtherProfile = viewerUser && viewerUser.id !== id;
        
        if (isViewingOtherProfile) {
          // When viewing another user's profile, show protocols they created with deduplication
          const createdProtocolsCacheKey = getCacheKey('fetchCreatedProtocols', id);
          const createdProtocols = await dedupeRequest(createdProtocolsCacheKey, async () => {
            const { data, error } = await supabase
              .from('protocols')
              .select(`
              *,
              adherence_score,
              pop_score,
              protocol_hacks ( 
                hack_id, 
                position,
                biohacks (
                  id,
                  name,
                  image_url,
                  category
                )
              ),
              protocol_ratings ( id, rating ),
              user_profiles!protocols_created_by_fkey (
                user_id,
                username
              )
            `)
            .eq('created_by', id);
            if (error) throw error;
            return data;
          });
          
          // Fetch the viewed user's protocols_status to get actual active/staged status
          const otherUserProtocolStatusCacheKey = getCacheKey('fetchOtherUserProtocolStatus', id);
          const otherUserProtocolStatus = await dedupeRequest(otherUserProtocolStatusCacheKey, async () => {
            const { data, error } = await supabase
              .from('protocols_status')
              .select('id, protocol_id, is_active, activated_at, deactivated_at, added_at')
              .eq('user_id', id);
            if (error) throw error;
            return data || [];
          });
          
          // Create a map of protocol_id -> status for quick lookup
          const statusMap = new Map();
          (otherUserProtocolStatus || []).forEach(status => {
            statusMap.set(status.protocol_id, status);
          });
          
          // Get the viewed user's username from the first protocol or use username from route
          const viewedUserUsername = createdProtocols?.[0]?.user_profiles?.username || username || 'Unknown Creator';
          
          // Format the data to match expected structure
          const protocolsWithHacks = (createdProtocols || []).map(protocol => {
            // Get creator username from joined data or use viewed user's username
            const creatorUsername = protocol.user_profiles?.username || viewedUserUsername;
            
            // Get the status record for this protocol
            const statusRecord = statusMap.get(protocol.id);
            
            // Determine status: if user has added this protocol, use their is_active status, otherwise default to 'staged'
            let protocolStatus = 'staged';
            if (statusRecord) {
              const isActive = statusRecord.is_active === true || statusRecord.is_active === 'true' || statusRecord.is_active === 1;
              protocolStatus = isActive ? 'active' : 'staged';
            }
            
            return {
              ...protocol,
              created_by_username: creatorUsername,
              status: protocolStatus,
              activated_at: statusRecord?.activated_at || null,
              deactivated_at: statusRecord?.deactivated_at || null,
              added_at: statusRecord?.added_at || null,
              user_protocol_status_id: statusRecord?.id || null,
              isCreatedByUser: true // Flag to indicate this protocol was created by the viewed user
            };
          });
          
          setProtocols(protocolsWithHacks);
          
          // Also fetch all protocols this user has added for activity display with deduplication
          const userProtocolStatusesCacheKey = getCacheKey('fetchUserProtocolStatuses', id);
          const userProtocolStatuses = await dedupeRequest(userProtocolStatusesCacheKey, async () => {
            const { data, error } = await supabase
              .from('protocols_status')
              .select(`
              *,
              protocols:protocol_id (
                *,
                adherence_score,
                pop_score,
                protocol_hacks ( 
                  hack_id, 
                  position,
                  biohacks (
                    id,
                    name,
                    image_url,
                    category
                  )
                )
              )
            `)
            .eq('user_id', id);
            if (error) throw error;
            return data;
          });
          
          // Format for activity display
          const userProtocolsForActivity = (userProtocolStatuses || []).map(status => {
            const protocol = status.protocols;
            if (!protocol) return null;
            
            return {
              ...protocol,
              // Explicitly check is_active as boolean (handle string "true"/"false" or null)
              status: (status.is_active === true || status.is_active === 'true' || status.is_active === 1) ? 'active' : 'staged',
              activated_at: status.activated_at,
              deactivated_at: status.deactivated_at,
              added_at: status.added_at,
              user_protocol_status_id: status.id
            };
          }).filter(Boolean);
          
          // Store for activity display
          setAllUserProtocols(userProtocolsForActivity);
          
          // Extract unique biohack IDs from all protocols to fetch only needed biohacks
          // Only fetch biohacks if we have protocols (they're only needed for activity/protocols tabs)
          const allProtocols = [...protocolsWithHacks, ...userProtocolsForActivity];
          const biohackIds = new Set();
          if (allProtocols.length > 0) {
            allProtocols.forEach(protocol => {
              if (protocol.protocol_hacks) {
                protocol.protocol_hacks.forEach(ph => {
                  if (ph.biohacks && ph.biohacks.id) {
                    biohackIds.add(ph.biohacks.id);
                  } else if (ph.hack_id) {
                    biohackIds.add(ph.hack_id);
                  }
                });
              }
              if (protocol.hacks) {
                protocol.hacks.forEach(hack => {
                  if (hack.id) biohackIds.add(hack.id);
                });
              }
            });
            
            // Fetch only the biohacks used in this user's protocols (not all 86+ biohacks)
            // This is deferred until protocols are loaded and only if protocols exist
            if (biohackIds.size > 0) {
              const { data: userBiohacks, error: biohacksError } = await supabase
                .from('biohacks')
                .select('id, name, image_url, category, description')
                .in('id', Array.from(biohackIds));
              
              if (biohacksError) {
                console.error('Error fetching user biohacks:', sanitizeErrorForLog(biohacksError));
                setBiohacks([]);
              } else {
                setBiohacks(userBiohacks || []);
              }
            } else {
              setBiohacks([]);
            }
          } else {
            // No protocols, so no need to fetch biohacks
            setBiohacks([]);
          }
          
          return;
        }
        
        // When viewing own profile, show user's protocol statuses
        // Creator usernames come from the protocol query join (user_profiles!protocols_created_by_fkey);
        // we do not fetch admin list, to avoid exposing who is an admin to all users.
        // Fetch protocol status data with deduplication (includes protocol_ratings in join)
        const protocolStatusCacheKey = getCacheKey('fetchProtocolStatusData', id);
        const protocolStatusData = await dedupeRequest(protocolStatusCacheKey, async () => {
          const { data, error } = await supabase
            .from('protocols_status')
            .select(`
            id,
            user_id,
            protocol_id,
            added_at,
            activated_at,
            deactivated_at,
            is_active,
            protocols:protocol_id (
              *,
              adherence_score,
              pop_score,
              protocol_hacks ( 
                hack_id, 
                position,
                biohacks (
                  id,
                  name,
                  image_url,
                  category
                )
              ),
              protocols_status!inner ( user_id, is_active ),
              protocol_ratings ( id, rating ),
              user_profiles!protocols_created_by_fkey (
                user_id,
                username
              )
            )
          `)
          .eq('user_id', id);
          if (error) throw error;
          return data;
        });
        
        // Join protocol_hacks to biohacks and format the data
        const protocolsWithHacks = (protocolStatusData || []).map(status => {
          const protocol = status.protocols;
          if (!protocol) return null;
          
          // Use nested biohacks from protocol query (already includes id, name, image_url, category)
          // Fallback to biohacks state if nested data not available (for backward compatibility)
          const hacks = (protocol.protocol_hacks || [])
            .sort((a, b) => a.position - b.position)
            .map(h => {
              // Prefer nested biohacks data (already fetched in protocol query)
              if (h.biohacks) {
                return h.biohacks;
              }
              // Fallback to biohacks state (for description field used in DayDetailsPanel)
              return biohacks.find(b => b.id === h.hack_id);
            })
            .filter(Boolean);
          
          // Count unique users who have added this protocol
          const usageCount = protocol.protocols_status ? protocol.protocols_status.length : 0;
          
          // Count active users for this protocol
          const activeCount = protocol.protocols_status 
            ? protocol.protocols_status.filter(s => s.is_active === true || s.is_active === 'true' || s.is_active === 1).length 
            : 0;
          
          // Count votes for this protocol
          const upvotesCount = protocol.protocol_ratings 
            ? protocol.protocol_ratings.filter(r => r.rating === 1).length 
            : 0;
          const downvotesCount = protocol.protocol_ratings 
            ? protocol.protocol_ratings.filter(r => r.rating === -1).length 
            : 0;
          const likesCount = protocol.protocol_ratings ? protocol.protocol_ratings.length : 0;
          
          // Count users who have added this protocol
          const usersCount = protocol.protocols_status ? protocol.protocols_status.length : 0;
          
          // Get creator username from joined user_profiles data (no admin list lookup)
          const creatorUsername = protocol.user_profiles?.username || 'Unknown Creator';
          
          // Determine status: explicitly check is_active (handle boolean true, string "true", number 1, or null/false)
          const isActive = status.is_active === true || status.is_active === 'true' || status.is_active === 1;
          const protocolStatus = isActive ? 'active' : 'staged';
          
          return {
            ...protocol,
            // Add status information from protocols_status
            status: protocolStatus,
            activated_at: status.activated_at,
            deactivated_at: status.deactivated_at,
            added_at: status.added_at,
            user_protocol_status_id: status.id, // Keep reference to status record
            hacks,
            // Add stats like catalogue
            usage_count: usageCount,
            active_count: activeCount,
            likes_count: likesCount,
            users_count: usersCount,
            upvotes_count: upvotesCount,
            downvotes_count: downvotesCount,
            created_by_username: creatorUsername
          };
        }).filter(Boolean);
        
        // Use trending_rank from database instead of calculating
        const protocolsWithRanks = protocolsWithHacks
          .map((protocol) => ({
            ...protocol,
            activeness_rank: protocol.trending_rank || 999 // Use trending_rank from database, fallback to 999
          }))
          .sort((a, b) => a.id - b.id); // Sort back by protocol ID for consistent display
        
        // Split and sort protocols
        const active = protocolsWithRanks.filter(p => p.status === 'active').sort((a, b) => new Date(a.activated_at) - new Date(b.activated_at));
        const inactive = protocolsWithRanks.filter(p => p.status === 'staged').sort((a, b) => new Date(b.added_at) - new Date(a.added_at));
        setProtocols([...active, ...inactive]);
        
        // For own profile, allUserProtocols is the same as protocols
        setAllUserProtocols(protocolsWithRanks);
        
        // Extract unique biohack IDs from all protocols to fetch only needed biohacks
        // Only fetch biohacks if we have protocols (they're only needed for activity/protocols tabs)
        const biohackIds = new Set();
        if (protocolsWithRanks.length > 0) {
          protocolsWithRanks.forEach(protocol => {
            if (protocol.protocol_hacks) {
              protocol.protocol_hacks.forEach(ph => {
                if (ph.biohacks && ph.biohacks.id) {
                  biohackIds.add(ph.biohacks.id);
                } else if (ph.hack_id) {
                  biohackIds.add(ph.hack_id);
                }
              });
            }
            if (protocol.hacks) {
              protocol.hacks.forEach(hack => {
                if (hack.id) biohackIds.add(hack.id);
              });
            }
          });
          
          // Fetch only the biohacks used in this user's protocols (not all 86+ biohacks)
          // This is deferred until protocols are loaded and only if protocols exist
          if (biohackIds.size > 0) {
            const { data: userBiohacks, error: biohacksError } = await supabase
              .from('biohacks')
              .select('id, name, image_url, category, description')
              .in('id', Array.from(biohackIds));
            
            if (biohacksError) {
              console.error('Error fetching user biohacks:', sanitizeErrorForLog(biohacksError));
              setBiohacks([]);
            } else {
              setBiohacks(userBiohacks || []);
            }
          } else {
            setBiohacks([]);
          }
        } else {
          // No protocols, so no need to fetch biohacks
          setBiohacks([]);
        }
        
        // Fetch schedule data for protocols with deduplication
        if (id && typeof id === 'string' && id !== 'undefined' && id !== 'null' && protocolsWithRanks.length > 0) {
          try {
            const protocolIds = protocolsWithRanks.map(p => p.id);
            const schedulesCacheKey = getCacheKey('fetchLoggingSchedules', `${id}_${protocolIds.sort().join(',')}`);
            const scheduleData = await dedupeRequest(schedulesCacheKey, async () => {
              const { data, error } = await supabase
                .from('logging_schedules')
                .select('protocol_id, hack_id, time, unit, number')
                .eq('user_id', id)
                .eq('is_active', true)
                .in('protocol_id', protocolIds);
              if (error) throw error;
              return data;
            });

            if (scheduleData) {
              // Build schedule map: { protocolId: { hackId: { frequency, times } } }
              // Note: For backward compatibility, we derive frequency from unit/number
              // unit can be: 'once', 'daily', 'weekly', 'monthly', 'days', 'weeks', 'months'
              const scheduleMap = {};
              scheduleData.forEach(entry => {
                const protocolId = String(entry.protocol_id);
                const hackId = entry.hack_id ? String(entry.hack_id) : null;
                if (!hackId) return;

                if (!scheduleMap[protocolId]) {
                  scheduleMap[protocolId] = {};
                }
                if (!scheduleMap[protocolId][hackId]) {
                  // Derive frequency from unit (for backward compatibility)
                  // If unit is 'days', 'weeks', 'months', treat as 'daily', 'weekly', 'monthly' respectively
                  let frequency = entry.unit || 'daily';
                  if (frequency === 'days') frequency = 'daily';
                  else if (frequency === 'weeks') frequency = 'weekly';
                  else if (frequency === 'months') frequency = 'monthly';
                  
                  scheduleMap[protocolId][hackId] = {
                    frequency: frequency,
                    times: []
                  };
                }
                if (entry.time) {
                  scheduleMap[protocolId][hackId].times.push(entry.time);
                }
              });
              setProtocolSchedules(scheduleMap);
            }
          } catch (scheduleErr) {
            console.error('Error fetching schedule data:', sanitizeErrorForLog(scheduleErr));
          }
        }
        
      } catch (error) {
        console.error('Error fetching protocols:', sanitizeErrorForLog(error));
        setProtocols([]);
        setAllUserProtocols([]);
      } finally {
        setProtocolsLoading(false);
        fetchProtocolsInProgressRef.current = false;
      }
    };
    fetchProtocols();
  }, [userId, canFetchSensitiveData, visibleTabs]);


  // Opt-in handler
  const handleOptInChallenge = async (challenge) => {
    setOptingInId(challenge.id);
    try {
      let id = userId;
      if (!id) {
        const user = currentUser || await getCurrentUser();
        if (!user) return;
        id = user.id;
      }
      // Calculate retroactive progress
      let progressValue = 0;
      let completed = false;
      const { data: targets } = await supabase
        .from('challenge_targets')
        .select('*')
        .eq('challenge_id', challenge.id);
      if (targets && targets.length > 0) {
        const target = targets[0];
        if (target.target_type === 'biohack' && target.aggregation === 'count') {
          // Use biohack_id directly from the challenge target
          if (target.biohack_id) {
            const { data } = await supabase
              .from('logging_hack_entries')
              .select('*')
              .eq('user_id', id)
              .eq('hack_id', target.biohack_id)
              .gte('date', challenge.start_date)
              .lte('date', challenge.end_date);
            progressValue = data ? data.length : 0;
            completed = evaluateChallengeTarget(progressValue, target.operator, target.target_value);
          }
        }
      }
      const { error } = await supabase
        .from('user_challenge_progress')
        .insert({
          user_id: id,
          challenge_id: challenge.id,
          joined_at: new Date().toISOString(),
          progress_value: progressValue,
          completed,
          completed_at: completed ? new Date().toISOString() : null
        });
      if (error) throw error;
      // Challenges will be refreshed by ChallengesTab component
    } catch (err) {
      alert('Error joining challenge. Please try again.');
      console.error('Opt-in error:', sanitizeErrorForLog(err));
    } finally {
      setOptingInId(null);
    }
  };

  // Add leave challenge handler
  const handleLeaveChallenge = async (progressId) => {
    try {
      await supabase
        .from('user_challenge_progress')
        .delete()
        .eq('id', progressId);
      // Challenges will be refreshed by ChallengesTab component
    } catch (err) {
      alert('Error leaving challenge. Please try again.');
      console.error('Leave challenge error:', sanitizeErrorForLog(err));
    }
  };

  // Challenges are now fetched by ChallengesTab component, no need to fetch here

  // Helper to fetch like counts, liked status, and comment counts for forum posts
  const fetchLikesAndCommentsForForumPosts = useCallback(async (postIds) => {
    if (!postIds.length) return { likeCounts: {}, likedMap: {}, commentCounts: {} };
    // Get current user
    const user = currentUser || await getCurrentUser();
    let userId = user?.id;
    
    // Fetch all post_likes for these posts with deduplication
    const likesCacheKey = getCacheKey('fetchPostLikesForForumPosts', postIds.sort().join(','));
    const likeRows = await dedupeRequest(likesCacheKey, async () => {
      const { data, error } = await supabase
        .from('post_likes')
        .select('post_id, user_id, id')
        .in('post_id', postIds);
      if (error) throw error;
      return data;
    });
    
    // Fetch all post_comments for these posts (excluding deleted ones) with deduplication
    const commentsCacheKey = getCacheKey('fetchPostCommentsForForumPosts', postIds.sort().join(','));
    const commentRows = await dedupeRequest(commentsCacheKey, async () => {
      const { data, error } = await supabase
        .from('post_comments')
        .select('post_id')
        .in('post_id', postIds)
        .eq('is_deleted', false);
      if (error) throw error;
      return data;
    });
    // Count likes per post
    let likeCounts = {};
    let likedMap = {};
    (likeRows || []).forEach(row => {
      likeCounts[row.post_id] = (likeCounts[row.post_id] || 0) + 1;
      if (userId && row.user_id === userId) {
        likedMap[row.post_id] = row.id;
      }
    });
    // Count comments per post
    let commentCounts = {};
    (commentRows || []).forEach(row => {
      commentCounts[row.post_id] = (commentCounts[row.post_id] || 0) + 1;
    });
    return { likeCounts, likedMap, commentCounts };
  }, [getCurrentUser]);

  // Move fetchForumPosts outside useEffect to make it accessible
  const fetchForumPosts = useCallback(async () => {
    setForumPostsLoading(true);
    setForumPostsError(null);
    let id = userId;
    if (!id) {
      const user = currentUser || await getCurrentUser();
      if (!user) {
        setForumPosts([]);
        setForumPostsLoading(false);
        return;
      }
      id = user.id;
    }
    
    try {
      // Use the same comprehensive query as PostFeed to get all attachments with deduplication
      // Note: Visibility filtering is handled by RLS policies, which allow users to see:
      // - Public posts (for everyone)
      // - Their own posts (regardless of visibility)
      const postsCacheKey = getCacheKey('fetchForumPosts', id);
      const data = await dedupeRequest(postsCacheKey, async () => {
        const { data, error } = await supabase
          .from('posts')
          .select(`*, user_profiles!posts_user_id_fkey(username, user_level, profile_picture_url),
            post_attachments:post_attachments_post_id_fkey(
              id, type, sort_order,
              post_attachment_protocols:post_attachment_protocols_post_attachment_id_fkey(
                protocols:protocol_id(
                  id, name, description, created_at, adherence_score, pop_score,
                  protocol_hacks:protocol_hacks_protocol_id_fkey(
                    id, position,
                    biohacks:protocol_hacks_hack_id_fkey(id, name, image_url, category)
                  ),
                  protocol_ratings ( id, rating ),
                  user_profiles!protocols_created_by_fkey(user_id, username)
                )
              ),
              post_attachment_images:post_attachment_images_post_attachment_id_fkey(
                id, image_url, file_name, mime_type
              ),
              post_attachment_reports:post_attachment_reports_post_attachment_id_fkey(
                protocol_report_snapshots:report_id(
                  id, protocol_id, title, snapshot_date, created_at
                )
              )
            ),
            post_biohack_tags:post_biohack_tags_post_id_fkey(
              biohacks:biohack_id(id, name, image_url)
            )
          `)
          .eq('user_id', id)
          .eq('is_deleted', false)
          .lte('created_at', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(5);
        if (error) throw error;
        return data;
      });

      // Fetch biomarker attachments and merge into posts (same as PostFeed) so biomarker attachments display on user page
      let postsWithBiomarkers = data || [];
      const postIds = (data || []).map((p) => p.id);
      if (postIds.length > 0) {
        const biomarkerData = await fetchBiomarkerAttachments(postIds, data || []);
        postsWithBiomarkers = (data || []).map((post) => {
          if (biomarkerData[post.id]) {
            const attachmentMap = new Map((post.post_attachments || []).map((att) => [att.id, att]));
            Object.values(biomarkerData[post.id]).forEach(({ attachment, biomarkers }) => {
              const existingAttachment = attachmentMap.get(attachment.id);
              const enriched = {
                ...(existingAttachment || attachment),
                ...attachment,
                post_attachment_biomarkers: biomarkers.map((bm) => ({
                  biomarker_id: bm.id,
                  biomarkers: bm
                }))
              };
              attachmentMap.set(attachment.id, enriched);
            });
            return {
              ...post,
              post_attachments: Array.from(attachmentMap.values())
            };
          }
          return post;
        });
      }

      // Transform the data to match the structure expected by PostCard
      const transformedPosts = postsWithBiomarkers.map((post) => ({
        ...post,
        attachedProtocols: (post.post_attachments || [])
          .filter((att) => att.type === 'protocols')
          .flatMap((att) => (att.post_attachment_protocols || [])
            .map((pap) => pap.protocols)
          )
      }));
      setForumPosts(transformedPosts);
      
      // Fetch likes and comments data for these posts
      const forumPostIds = transformedPosts.map((post) => post.id);
      if (forumPostIds.length > 0) {
        const { likeCounts, likedMap, commentCounts } = await fetchLikesAndCommentsForForumPosts(forumPostIds);
        setForumPostsLikesInfo({
          likeCounts,
          likedMap,
          commentCounts
        });
      }
    } catch (error) {
      console.error('Forum posts fetch error:', sanitizeErrorForLog(error));
      setForumPostsError('Failed to load forum posts');
      setForumPosts([]);
    } finally {
      setForumPostsLoading(false);
    }
  }, [userId, getCurrentUser, fetchLikesAndCommentsForForumPosts]);

  useEffect(() => {
    if (!canFetchSensitiveData) {
      if (visibleTabs !== null) {
        setForumPosts([]);
        setForumPostsError(null);
        setForumPostsLoading(false);
      }
      return;
    }
    fetchForumPosts();
  }, [fetchForumPosts, canFetchSensitiveData, visibleTabs]);

  // Close biotag filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (biotagFilterRef.current && !biotagFilterRef.current.contains(event.target)) {
        setShowBiotagFilter(false);
      }
    };

    if (showBiotagFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBiotagFilter]);

  // Fetch followers and following only when viewer is allowed to see them (owner or accepted follower)
  const canViewFollowersFollowing = isOwnProfile || (isFollowing && !isFollowPending);
  useEffect(() => {
    if (!canViewFollowersFollowing) {
      setFollowers([]);
      setFollowing([]);
      setFollowersLoading(false);
      setFollowingLoading(false);
      return;
    }
    if (!(userId || currentUserId) || userId === 'undefined') return;
    const timer = setTimeout(() => {
      fetchFollowers();
      fetchFollowing();
    }, 100);
    return () => clearTimeout(timer);
  }, [userId, currentUserId, canViewFollowersFollowing, fetchFollowers, fetchFollowing]);

  // Profile picture upload handler
  const handleAvatarClick = () => {
    if (isOwnProfile && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Delete profile picture
  const handleDeleteProfilePicture = async () => {
    if (!isOwnProfile) return;
    
    setDeletingProfilePicture(true);
    try {
      const targetUserId = userId || currentUserId;
      if (!targetUserId) {
        throw new Error('No user ID available');
      }

      // Update user profile to remove profile picture URL
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ profile_picture_url: null })
        .eq('user_id', targetUserId);

      if (updateError) {
        console.error('Profile update error:', sanitizeErrorForLog(updateError));
        throw new Error('Failed to remove profile picture. Please try again.');
      }

      // Update local state
      setUserData(prev => prev ? { ...prev, profile_picture_url: null } : null);
      setShowDeleteConfirmation(false);

    } catch (error) {
      console.error('Error removing profile picture:', sanitizeErrorForLog(error));
      alert(getUserFacingErrorMessage(error, 'Failed to remove profile picture. Please try again.'));
    } finally {
      setDeletingProfilePicture(false);
    }
  };

  // Schedule slide-over handlers
  const handleOpenScheduleSlideOver = (protocol) => {
    setSelectedProtocol(protocol);
    setScheduleSlideOverOpen(true);
  };

  const handleCloseScheduleSlideOver = () => {
    setScheduleSlideOverOpen(false);
    setSelectedProtocol(null);
  };

  // Listen for custom event to open schedule from ChecklistLogger
  useEffect(() => {
    const handleOpenScheduleEvent = async (event) => {
      const { protocolId, protocol } = event.detail;
      if (!protocolId) return;

      // Always prefer finding the protocol from UserPage's state first,
      // as it has the complete structure with protocol_hacks and biohacks
      const findProtocol = () => {
        return allUserProtocols.find(p => String(p.id) === String(protocolId)) ||
               protocols.find(p => String(p.id) === String(protocolId));
      };

      let targetProtocol = findProtocol();
      
      // If not found and we have a protocol from event, try to fetch full protocol data
      if (!targetProtocol && protocol) {
        // Fetch the full protocol with all biohack data
        try {
          const { data: fullProtocol, error } = await supabase
            .from('protocols')
            .select(`
              *,
              adherence_score,
              pop_score,
              protocol_hacks ( 
                hack_id, 
                position,
                biohacks (
                  id,
                  name,
                  image_url,
                  category,
                  description,
                  ease_of_use_score,
                  time_efficiency_score,
                  affordability_score
                )
              ),
              protocol_ratings ( id, rating ),
              user_profiles!protocols_created_by_fkey (
                user_id,
                username
              )
            `)
            .eq('id', protocolId)
            .single();

          if (!error && fullProtocol) {
            // Format it like UserPage protocols
            targetProtocol = {
              ...fullProtocol,
              created_by_username: fullProtocol.user_profiles?.username || 'Unknown'
            };
          }
        } catch (fetchError) {
          console.error('Error fetching protocol for schedule:', sanitizeErrorForLog(fetchError));
          // Fall back to protocol from event
          targetProtocol = protocol;
        }
      }
      
      if (targetProtocol) {
        handleOpenScheduleSlideOver(targetProtocol);
      } else {
        // If still not found, wait a bit and try again (protocols might still be loading)
        setTimeout(() => {
          const retryProtocol = findProtocol();
          if (retryProtocol) {
            handleOpenScheduleSlideOver(retryProtocol);
          } else if (protocol) {
            // Last resort: use protocol from event
            handleOpenScheduleSlideOver(protocol);
          }
        }, 500);
      }
    };

    window.addEventListener('open-protocol-schedule', handleOpenScheduleEvent);
    return () => {
      window.removeEventListener('open-protocol-schedule', handleOpenScheduleEvent);
    };
  }, [allUserProtocols, protocols]);

  // Notes slide-over handlers
  const handleOpenNotesSlideOver = (protocol) => {
    setSelectedProtocolForNotes(protocol);
    setNotesSlideOverOpen(true);
  };

  const handleCloseNotesSlideOver = () => {
    setNotesSlideOverOpen(false);
    setSelectedProtocolForNotes(null);
  };

  // Handle adding protocol to staged protocols (from another user's profile)
  const handleAddProtocolToStaged = useCallback(async (protocol) => {
    if (!currentUserId) {
      setSuccessMessage('Please sign in to add protocols');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      return false;
    }

    if (!protocol || !protocol.id) {
      setSuccessMessage('Invalid protocol');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      return false;
    }

    try {
      if (hasProtocol(protocol.id)) {
        setSuccessMessage('Protocol already in your profile');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        return true;
      }

      const protocolHacks = protocol.biohacks || protocol.hacks || protocol.protocol_hacks || [];
      if (protocolHacks.length === 0) {
        setSuccessMessage('Cannot add a protocol with no biohacks');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        return false;
      }

      // Create protocol status record for the user (staged = is_active: false)
      const { error: statusError } = await supabase
        .from('protocols_status')
        .insert({
          user_id: currentUserId,
          protocol_id: protocol.id,
          is_active: false,
          added_at: new Date().toISOString()
        });

      if (statusError) {
        if (statusError.code === '23505') {
          await refreshProtocolStatuses();
          setSuccessMessage('Protocol already in your profile');
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 3000);
          return true;
        }
        console.error('Error adding protocol to staged:', sanitizeErrorForLog(statusError));
        setSuccessMessage('Error adding protocol. Please try again.');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        return false;
      }

      await refreshProtocolStatuses();
      setSuccessMessage('Protocol added to your staged protocols!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      return true;
    } catch (error) {
      console.error('Error adding protocol to staged:', sanitizeErrorForLog(error));
      setSuccessMessage('Error adding protocol. Please try again.');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      return false;
    }
  }, [currentUserId, hasProtocol, refreshProtocolStatuses]);

  const handleSaveSchedule = async (protocolId, scheduleData) => {
    try {
      if (!currentUserId) {
        throw new Error('User not authenticated');
      }

      // First, mark existing active schedules for this protocol as inactive
      const { error: updateError } = await supabase
        .from('logging_schedules')
        .update({ is_active: false })
        .eq('user_id', currentUserId)
        .eq('protocol_id', protocolId)
        .eq('is_active', true);

      if (updateError) {
        console.error('Failed to deactivate existing schedules:', sanitizeErrorForLog(updateError));
        throw new Error('Failed to update schedules');
      }

      // Prepare new schedule entries
      const scheduleEntries = [];

      Object.entries(scheduleData).forEach(([hackId, schedule]) => {
        if (schedule.times && schedule.times.length > 0 && schedule.interval !== null && schedule.interval !== undefined && schedule.intervalUnit) {
          schedule.times.forEach(timeEntry => {
            scheduleEntries.push({
              user_id: currentUserId,
              protocol_id: protocolId,
              hack_id: hackId,
              time: timeEntry,
              unit: schedule.intervalUnit, // 'days', 'weeks', 'months'
              number: schedule.interval, // e.g., 2 for "every 2 days"
              is_active: true
            });
          });
        }
      });

      // Insert new schedules if any
      if (scheduleEntries.length > 0) {
        const { error: insertError } = await supabase
          .from('logging_schedules')
          .insert(scheduleEntries);

        if (insertError) {
          console.error('Failed to insert new schedules:', sanitizeErrorForLog(insertError));
          throw new Error('Failed to save schedules');
        }
      }

      // Dispatch event to notify other components
      window.dispatchEvent(new Event('logging-schedule-updated'));

      return true;
    } catch (error) {
      console.error('Failed to save schedule:', sanitizeErrorForLog(error));
      throw error;
    }
  };

  // Profile picture menu functions removed - now handled in Edit Profile modal

  // Check if storage bucket exists (optional validation)
  const checkStorageBucket = async () => {
    try {
      // Check if we can access the user-profile-pictures bucket
      const { data, error } = await supabase.storage
        .from('user-profile-pictures')
        .list('', { limit: 1 });
      
      if (error) {
        console.warn('Could not access user-profile-pictures bucket:', sanitizeErrorForLog(error));
        return false;
      }
      
      return true;
    } catch (error) {
      console.warn('Error checking storage bucket:', sanitizeErrorForLog(error));
      return false;
    }
  };

  // Enhanced profile picture upload with bucket check
  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP).');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB.');
      return;
    }

    // Additional validation: check if file is actually an image
    if (file.size === 0) {
      alert('Please select a valid image file.');
      return;
    }

    // Show loading state immediately
    setUploadingProfilePicture(true);

    try {
      const targetUserId = userId || currentUserId;
      if (!targetUserId) {
        throw new Error('No user ID available');
      }

      // Check if we can access the user-profile-pictures bucket
      const bucketAccessible = await checkStorageBucket();
      if (!bucketAccessible) {
        throw new Error('Cannot access storage bucket. Please contact support.');
      }

      // Generate unique filename with better naming
      const fileExt = file.name.split('.').pop().toLowerCase();
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8);
      const fileName = `profile-${targetUserId}-${timestamp}-${randomId}.${fileExt}`;

      // Upload to Supabase storage using user-profile-pictures bucket
      // Use 7 days cache (604800 seconds) - profile pictures may change more frequently than post images
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user-profile-pictures')
        .upload(fileName, file, {
          cacheControl: '604800', // 7 days - profile pictures may change more frequently
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error details:', sanitizeErrorForLog(uploadError));
        
        // Provide more specific error messages
        if (uploadError.message.includes('row-level security policy')) {
          throw new Error('Permission denied: Storage bucket RLS policy prevents upload. Please contact support to configure proper permissions.');
        } else if (uploadError.message.includes('bucket')) {
          throw new Error('Storage bucket not found. Please contact support.');
        } else if (uploadError.message.includes('permission')) {
          throw new Error('Permission denied. Please contact support.');
        } else if (uploadError.statusCode === '403') {
          throw new Error('Access forbidden. The storage bucket may not allow uploads or may have restrictive RLS policies.');
        } else {
          throw new Error(getUserFacingErrorMessage(uploadError, 'Upload failed. Please try again.'));
        }
      }

      // Get public URL
      const { data: { publicUrl } = {} } = supabase.storage
        .from('user-profile-pictures')
        .getPublicUrl(fileName);

      // Update user profile with new picture URL
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ profile_picture_url: publicUrl })
        .eq('user_id', targetUserId);

      if (updateError) {
        console.error('Profile update error:', sanitizeErrorForLog(updateError));
        throw new Error('Failed to update profile. Please try again.');
      }

      // Update local state
      setUserData(prev => prev ? { ...prev, profile_picture_url: publicUrl } : null);
      setProfilePictureUploaded(true);
    } catch (error) {
      console.error('Error uploading profile picture:', sanitizeErrorForLog(error));
      alert(getUserFacingErrorMessage(error, 'Failed to upload profile picture. Please try again.'));
    } finally {
      setUploadingProfilePicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Auto-hide success state after 3 seconds
  useEffect(() => {
    if (profilePictureUploaded) {
      const timer = setTimeout(() => {
        setProfilePictureUploaded(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [profilePictureUploaded]);

  // Bio save handler
  const handleBioSave = async () => {
    if (!isOwnProfile) return;
    
    setSavingBio(true);
    try {
      const targetUserId = userId || currentUserId;
      if (!targetUserId) {
        throw new Error('No user ID available');
      }

      const { error } = await supabase
        .from('user_profiles')
        .update({ bio: bioText.trim() || null })
        .eq('user_id', targetUserId);

      if (error) {
        console.error('Bio update error:', sanitizeErrorForLog(error));
        throw new Error('Failed to update bio. Please try again.');
      }

      // Update local state
      setUserData(prev => prev ? { ...prev, bio: bioText.trim() || null } : null);
      setIsEditingBio(false);
    } catch (error) {
      console.error('Error updating bio:', sanitizeErrorForLog(error));
      alert(getUserFacingErrorMessage(error, 'Failed to update bio. Please try again.'));
    } finally {
      setSavingBio(false);
    }
  };

  // Bio cancel handler
  const handleBioCancel = () => {
    setBioText(userData?.bio || '');
    setIsEditingBio(false);
  };

  // No top-level loading splash; render the page structure immediately

  // Handle protocol like toggle (wrapper for hook)
  const handleToggleLike = async (protocol) => {
    await handleToggleLikeAction(
      protocol,
      (result) => {
        // Update protocols state on success
        setProtocols(prev => prev.map(p => 
          p.id === result.protocol_id 
            ? { ...p, likes_count: result.likes_count }
            : p
        ));
      },
      (errorMsg) => {
        setSuccessMessage(errorMsg);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    );
  };

  // Handle upvote/downvote protocol (wrapper for hook)
  const handleVote = async (protocol, voteType) => {
    // Optimistically update UI for immediate feedback
    setProtocols(prev => prev.map(p => {
      if (p.id === protocol.id) {
        const currentVote = userVotes[protocol.id];
        let newUpvotes = p.upvotes_count || 0;
        let newDownvotes = p.downvotes_count || 0;
        
        if (currentVote === 1) newUpvotes -= 1;
        else if (currentVote === -1) newDownvotes -= 1;
        
        const newVote = currentVote === voteType ? null : voteType;
        if (newVote === 1) newUpvotes += 1;
        else if (newVote === -1) newDownvotes += 1;
        
        return { ...p, upvotes_count: newUpvotes, downvotes_count: newDownvotes };
      }
      return p;
    }));
    
    await handleVoteAction(
      protocol,
      voteType,
      (result) => {
        // Update protocols state on success
        setProtocols(prev => prev.map(p => 
          p.id === result.protocol_id 
            ? { ...p, upvotes_count: result.upvotes_count, downvotes_count: result.downvotes_count }
            : p
        ));
      },
      (errorMsg) => {
        setSuccessMessage(errorMsg);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    );
  };

  // Handle like protocol (rating = 1)
  const handleLike = async (protocol) => {
    await handleVote(protocol, 1);
  };

  // Handle dislike protocol (rating = -1)
  const handleDislike = async (protocol) => {
    await handleVote(protocol, -1);
  };


  if (!loading && !userData) return <div className="user-page-error">User not found.</div>;

  return (
    <div className="user-page">
      <TopNavigation activePage="user" />
      <div className="page-container">
        {/* Hidden file input for profile picture upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleProfilePictureUpload}
          style={{ display: 'none' }}
        />


        {/* Delete Confirmation Modal - Profile Picture */}
        {showDeleteConfirmation && (
          <div className="delete-confirmation-overlay" onClick={() => setShowDeleteConfirmation(false)}>
            <div className="delete-confirmation-modal" onClick={e => e.stopPropagation()}>
              <div className="delete-confirmation-header">
                <Icon name="AlertTriangle" className="icon--md" />
                <h3>Remove Profile Picture?</h3>
              </div>
              <p>This will remove your current profile picture and reset it to the default avatar. This action cannot be undone.</p>
              
              <div className="delete-confirmation-actions">
                <button 
                  className="btn btn--secondary btn--md"
                  onClick={() => setShowDeleteConfirmation(false)}
                  disabled={deletingProfilePicture}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn--danger btn--md"
                  onClick={handleDeleteProfilePicture}
                  disabled={deletingProfilePicture}
                >
                  {deletingProfilePicture ? 'Removing...' : 'Remove Picture'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Full Page Profile Layout */}
        <UserProfilePage
          user={{
            user_id: userData?.user_id || userData?.id,
            id: userData?.user_id || userData?.id,
            username: username || 'Loading...',
            location: userData?.country || 'Location not set',
            country: userData?.country || null,
            bio: userData?.bio || 'Hey there! I\'m new here.',
            // Use list length when loaded (matches DB accepted follows); otherwise cached profile count
            followers_count: (canViewFollowersFollowing && !followersLoading) ? followers.length : (userData?.followers_count ?? followersCount ?? 0),
            following_count: (canViewFollowersFollowing && !followingLoading) ? following.length : (userData?.following_count ?? 0),
            avatar_url: userData?.profile_picture_url || null,
            followers: followers,
            following: following,
            user_level: userData?.user_level ?? 1,
            xp: userData?.xp ?? 0
          }}
          currentUserId={currentUserId}
          activity={activity}
          protocols={protocols}
          posts={forumPosts}
          tabPrivacySettings={tabPrivacySettings}
          visibleTabs={visibleTabs}
          onTabPrivacyChange={handleTabPrivacyChange}
          onFollowersClick={() => setShowFollowers(true)}
          onFollowingClick={() => setShowFollowing(true)}
          onMessage={handleMessage}
          onFollowToggle={handleFollowToggle}
          isFollowing={isFollowing}
          isFollowPending={isFollowPending}
          followLoading={followLoading}
          onSettingsClick={() => {
            setProfileSettings({
              email: userData?.email || '',
              username: username || '',
              country: userData?.country || ''
            });
            setShowProfileSettings(true);
          }}
        >
          {/* Activity Content */}
          <div className="user-profile-activity-content">
            {activityLoading ? (
              <div className="activity-section">
                <div className="activity-empty-state">
                  <JellyfishLoader />
                </div>
              </div>
            ) : (
              <div className="activity-section">
                <div className="activity-timeline">
                  <ActivityTimeline 
                    activity={activity} 
                    protocols={protocols} 
                    biohacks={biohacks}
                    onDayClick={handleDayClick}
                    selectedDay={selectedDay}
                    onBiohackClick={setSelectedBiohack}
                    onProtocolClick={setSelectedProtocol}
                    showAllBiohacks={showAllBiohacks}
                    setShowAllBiohacks={setShowAllBiohacks}
                    weekOffset={activityWeekOffset}
                    onWeekOffsetChange={setActivityWeekOffset}
                    allUserProtocols={allUserProtocols}
                    isOwnProfile={isOwnProfile}
                    profileUserId={userId || userData?.user_id || userData?.id || currentUserId}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Protocols Content */}
          <div className="user-profile-protocols-content">
            <div className="section">
              {protocolsLoading ? (
                <div className="protocols-loading-state">
                  <JellyfishLoader />
                </div>
              ) : protocols.length === 0 ? (
                <div className="protocols-empty-state">
                  {isOwnProfile ? (
                    <div className="protocols-empty-state-cta">
                      <p className="protocols-empty-state-text">No protocols yet.</p>
                      <button
                        className="btn btn--primary btn--md"
                        onClick={() => navigate('/protocols/create')}
                      >
                        Create Protocol
                      </button>
                    </div>
                  ) : (
                    'This user has no protocols yet.'
                  )}
                </div>
              ) : (
                <>
                  <div className="protocols-header">
                    <div className="protocols-filter-group">
                      <button
                        className={`protocols-filter-btn ${protocolsFilter === 'active' ? 'active' : ''}`}
                        onClick={() => setProtocolsFilter('active')}
                      >
                        Active
                      </button>
                      <button
                        className={`protocols-filter-btn ${protocolsFilter === 'staged' ? 'active' : ''}`}
                        onClick={() => setProtocolsFilter('staged')}
                      >
                        Staged
                      </button>
                    </div>
                  </div>

                  <div className="protocols-content">
                    {(() => {
                      // Filter and deduplicate by protocol ID
                      const protocolMap = new Map();
                      protocols.forEach(p => {
                        if (p.status === protocolsFilter && !protocolMap.has(p.id)) {
                          protocolMap.set(p.id, p);
                        }
                      });
                      const filteredProtocols = Array.from(protocolMap.values());
                      return filteredProtocols.length === 0 ? (
                        <div className="protocols-empty">
                          <h3>No {protocolsFilter} protocols</h3>
                          <p>
                            {isOwnProfile
                              ? (protocolsFilter === 'active'
                                  ? 'You have no active protocols. Activate a protocol to get started!'
                                  : 'You have no staged protocols yet.')
                              : (protocolsFilter === 'active'
                                  ? 'This user has no active protocols.'
                                  : 'This user has no staged protocols.')}
                          </p>
                        </div>
                      ) : (
                        <div className="protocols-grid">
                          {filteredProtocols.map((protocol) => (
                            <ProtocolShield
                              key={protocol.id}
                              protocol={protocol}
                              size="medium"
                              className="user-page-protocol-shield"
                              onScheduleClick={handleOpenScheduleSlideOver}
                              scheduleData={protocolSchedules[protocol.id] || {}}
                              onNotesClick={(isOwnProfile || protocol?.user_protocol_status_id) ? handleOpenNotesSlideOver : undefined}
                              onAddToStaged={!isOwnProfile ? handleAddProtocolToStaged : undefined}
                              onBiohackClick={setSelectedBiohack}
                              onActivateProtocol={isOwnProfile ? handleActivateProtocol : undefined}
                              onDeactivateProtocol={isOwnProfile ? handleDeactivateProtocol : undefined}
                              onDeleteProtocol={isOwnProfile && currentUserId && (protocol?.created_by === currentUserId || protocol?.user_protocol_status_id) ? handleDeleteProtocol : undefined}
                              onLike={handleLike}
                              onDislike={handleDislike}
                              userVote={userVotes[protocol.id] || null}
                            />
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>


          {/* Posts Content */}
          <div className="user-profile-posts-content">
            <div className="section">
              {forumPostsLoading ? (
                <div className="posts-loading-state">
                  <JellyfishLoader />
                </div>
              ) : forumPostsError ? (
                <div className="posts-error-state">
                  <Icon name="AlertTriangle" className="icon--lg" />
                  <p>{forumPostsError}</p>
                </div>
              ) : forumPosts.length === 0 ? (
                <div className="posts-empty-state">
                  No posts yet.
                </div>
              ) : (
                <>
                  {(() => {
                    // Extract unique biotags from all posts
                    const biotagsMap = new Map();
                    forumPosts.forEach(post => {
                      (post.post_biohack_tags || []).forEach(tag => {
                        if (tag.biohacks?.id) {
                          const biohackId = tag.biohacks.id;
                          if (!biotagsMap.has(biohackId)) {
                            biotagsMap.set(biohackId, {
                              id: biohackId,
                              name: tag.biohacks.name || 'Unknown',
                              image_url: tag.biohacks.image_url
                            });
                          }
                        }
                      });
                    });
                    const uniqueBiotags = Array.from(biotagsMap.values()).sort((a, b) => 
                      a.name.localeCompare(b.name)
                    );

                    // Filter posts based on selected biotags
                    const filteredPosts = selectedBiotags.length === 0
                      ? forumPosts
                      : forumPosts.filter(post => {
                          const postBiotagIds = (post.post_biohack_tags || [])
                            .map(tag => tag.biohacks?.id)
                            .filter(Boolean);
                          // Post must contain at least one of the selected biotags
                          return selectedBiotags.some(selectedId => postBiotagIds.includes(selectedId));
                        });

                    const postsToShow = showAllPosts ? filteredPosts : filteredPosts.slice(0, 4);

                    const handleBiotagToggle = (biotagId) => {
                      setSelectedBiotags(prev => 
                        prev.includes(biotagId)
                          ? prev.filter(id => id !== biotagId)
                          : [...prev, biotagId]
                      );
                    };

                    const handleClearFilters = () => {
                      setSelectedBiotags([]);
                      setShowBiotagFilter(false);
                    };

                    return (
                      <>
                        {uniqueBiotags.length > 0 && (
                          <div className="posts-header" ref={biotagFilterRef}>
                            <div className="posts-filter-group">
                              <button
                                className={`posts-filter-btn ${selectedBiotags.length === 0 ? 'active' : ''}`}
                                onClick={handleClearFilters}
                              >
                                All Posts
                              </button>
                              <div className="posts-filter-dropdown-wrapper">
                                <button
                                  className={`posts-filter-btn ${selectedBiotags.length > 0 ? 'active' : ''}`}
                                  onClick={() => setShowBiotagFilter(!showBiotagFilter)}
                                >
                                  Filter Posts
                                  {selectedBiotags.length > 0 && (
                                    <span className="posts-filter-count">({selectedBiotags.length})</span>
                                  )}
                                  <Icon name="ChevronDown" className="posts-filter-chevron" />
                                </button>
                                {showBiotagFilter && (
                                  <div className="posts-filter-dropdown">
                                    <div className="posts-filter-dropdown-header">
                                      <span>Select biotags to filter</span>
                                      {selectedBiotags.length > 0 && (
                                        <button
                                          className="posts-filter-clear"
                                          onClick={handleClearFilters}
                                        >
                                          Clear
                                        </button>
                                      )}
                                    </div>
                                    <div className="posts-filter-dropdown-list">
                                      {uniqueBiotags.map(biotag => {
                                        const isSelected = selectedBiotags.includes(biotag.id);
                                        return (
                                          <label
                                            key={biotag.id}
                                            className={`posts-filter-dropdown-item ${isSelected ? 'selected' : ''}`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => handleBiotagToggle(biotag.id)}
                                            />
                                            <span>{biotag.name}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="posts-grid">
                          {postsToShow.map(post => (
                      <PostCard
                        key={post.id}
                        post={{
                          ...post,
                          level: post.user_profiles?.user_level,
                          likes_count: forumPostsLikesInfo.likeCounts?.[post.id] || 0,
                          comments_count: forumPostsLikesInfo.commentCounts?.[post.id] || 0,
                          attachedProtocols: (post.post_attachments || [])
                            .filter(att => att.type === 'protocols')
                            .flatMap(att => (att.post_attachment_protocols || [])
                              .map(pap => pap.protocols)
                            )
                        }}
                        attachments={[]}
                        onCountsChanged={(data) => {
                          if (data && data.updatedPost) {
                            // This is a post update (e.g., edit), update the post in the feed
                            setForumPosts(prev => prev.map(p => p.id === data.updatedPost.id ? { ...p, ...data.updatedPost } : p));
                          } else {
                            // Refresh the forum posts when counts change
                            fetchForumPosts();
                          }
                        }}
                      />
                          ))}
                        </div>
                        {filteredPosts.length > 4 && (
                    <div className="posts-show-more">
                      <button 
                        className="posts-toggle-btn"
                        onClick={() => setShowAllPosts(!showAllPosts)}
                      >
                        {showAllPosts ? (
                          <>
                            <span>Show Less</span>
                            <Icon name="ChevronUp" className="icon--sm" />
                          </>
                        ) : (
                          <>
                            <span>Show {filteredPosts.length - 4} More Posts</span>
                            <Icon name="ChevronDown" className="icon--sm" />
                          </>
                        )}
                      </button>
                    </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          {/* Biomarkers Content */}
          <div className="user-profile-biomarkers-content">
            <LabsTab userId={userData?.user_id || userData?.id} isOwnProfile={isOwnProfile} />
          </div>

          {/* Challenges Content */}
          <div className="user-profile-challenges-content">
            <ChallengesTab userId={userData?.user_id || userData?.id} isOwnProfile={isOwnProfile} />
          </div>

          {/* Wearables Content */}
          <div className="user-profile-wearables-content">
            <WearablesTab userId={userData?.user_id || userData?.id} isOwnProfile={isOwnProfile} />
          </div>
        </UserProfilePage>

        {/* Schedule Slide-Over */}
        <ScheduleSlideOver
          isOpen={scheduleSlideOverOpen}
          onClose={handleCloseScheduleSlideOver}
          protocol={selectedProtocol}
          onSave={handleSaveSchedule}
          userId={currentUserId}
        />

        {/* Notes Slide-Over */}
        <ProtocolNotesSlideOver
          isOpen={notesSlideOverOpen}
          onClose={handleCloseNotesSlideOver}
          protocol={selectedProtocolForNotes}
          userId={currentUserId}
        />

        {/* Followers/Following Modal */}
        <FollowersModal 
          open={showFollowers} 
          onClose={() => setShowFollowers(false)} 
          list={followers} 
          title="Followers" 
          loading={followersLoading}
        />
        <FollowersModal 
          open={showFollowing} 
          onClose={() => setShowFollowing(false)} 
          list={following} 
          title="Following" 
          loading={followingLoading}
        />

        {/* Biohack Info Card Modal */}
        {selectedBiohack && (
          <BiohackInfoCard
            biohack={selectedBiohack}
            onClose={() => setSelectedBiohack(null)}
            onRatingSubmitted={() => {
              // Refresh data if needed
            }}
          />
        )}

        {/* Protocol Shield Display */}
        {selectedProtocol && (
          <div className="protocol-display-overlay" onClick={() => setSelectedProtocol(null)}>
            {scheduleSlideOverOpen ? (
              <div className="protocol-display-wrapper protocol-display-wrapper--schedule" onClick={e => e.stopPropagation()}>
                <div className="protocol-display-container protocol-display-container--schedule">
                  <ProtocolShield
                    protocol={selectedProtocol}
                    size="medium"
                    disableFlip={true}
                    hideFabMenu={true}
                    hideInspectButton={true}
                    onBiohackClick={setSelectedBiohack}
                  />
                </div>
              </div>
            ) : (
              <div className="protocol-display-container" onClick={e => e.stopPropagation()}>
                <ProtocolShield
                  protocol={selectedProtocol}
                  size="large"
                  onBiohackClick={setSelectedBiohack}
                  onNotesClick={(isOwnProfile || selectedProtocol?.user_protocol_status_id) ? handleOpenNotesSlideOver : undefined}
                  onAddToStaged={!isOwnProfile ? handleAddProtocolToStaged : undefined}
                  onActivateProtocol={isOwnProfile ? handleActivateProtocol : undefined}
                  onDeactivateProtocol={isOwnProfile ? handleDeactivateProtocol : undefined}
                  onLike={handleLike}
                  onDislike={handleDislike}
                  userVote={userVotes[selectedProtocol.id] || null}
                />
              </div>
            )}
          </div>
        )}

        {/* Profile Settings Modal */}
        {showProfileSettings && (
          <ProfileSettingsModal
            settings={profileSettings}
            onClose={() => setShowProfileSettings(false)}
            onSave={handleProfileSettingsSave}
            userData={userData}
            onProfilePictureUpload={handleProfilePictureUpload}
            onProfilePictureDelete={handleDeleteProfilePicture}
          />
        )}

        {/* Protocol Users Modal */}
        <FollowersModal 
          open={showProtocolUsers} 
          onClose={() => setShowProtocolUsers(false)} 
          list={selectedProtocolUsers} 
          title="Protocol Users" 
          loading={protocolUsersLoading}
        />

        {/* Success Message Indicator */}
        {showSuccessMessage && (
          <div className={`protocol-success-indicator ${showSuccessMessage === 'warning' ? 'warning' : ''}`}>
            <Icon name={showSuccessMessage === 'warning' ? "AlertTriangle" : "Check"} className="icon--sm" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Deactivation Confirmation Modal */}
        {showDeactivateModal && (
          <div className="modal-overlay" onClick={(e) => {
            // Don't close on overlay click - user must explicitly choose
            e.stopPropagation();
          }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Deactivate Protocol</h3>
              </div>
              <div className="modal-body" style={{ padding: '24px' }}>
                <div style={{ 
                  marginBottom: '24px', 
                  fontSize: '14px', 
                  lineHeight: '1.6', 
                  color: 'var(--color-text-primary)'
                }}>
                  <p style={{ marginBottom: '16px' }}>
                    Deactivating this protocol will have the following effects:
                  </p>
                  <ul style={{ marginLeft: '20px', marginBottom: '16px', padding: 0 }}>
                    <li style={{ marginBottom: '8px' }}>
                      <strong>Logging notifications will stop:</strong> You will no longer receive reminders for scheduled logging times while the protocol is inactive.
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong>Adherence calculations:</strong> Adherence scores are based on the current active period only. Once deactivated, the score reflects your most recent activation period. If you reactivate later, adherence will reset and only count from the new activation date forward. Logs from previous active periods are not included.
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong>Report periods:</strong> Protocol reports show the last 28 days of activity, but stats like days active and adherence are calculated from your most recent activation period only. Previous active periods are not included in these calculations.
                    </li>
                    <li style={{ marginBottom: '0' }}>
                      <strong>You can reactivate later:</strong> The protocol can be reactivated at any time, and logging can resume with the same schedule.
                    </li>
                  </ul>
                  <p style={{ marginTop: '16px', marginBottom: '0' }}>
                    Are you sure you want to deactivate <strong>{pendingDeactivateProtocol?.name || 'this protocol'}</strong>?
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="button"
                    className="btn btn--secondary btn--md btn--rounded"
                    onClick={handleCancelDeactivate}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--md btn--rounded"
                    onClick={handleConfirmDeactivate}
                    style={{ flex: 1 }}
                  >
                    Deactivate Protocol
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {showOnboarding && (
        <Onboarding onComplete={() => setShowOnboarding(false)} />
      )}
    </div>
  );
};

  // Helper function to calculate days left
  function getDaysLeft(endDate) {
    if (!endDate) return null;
    const now = new Date();
    const end = new Date(endDate);
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : 0;
  }

// Profile Settings Modal Component
function ProfileSettingsModal({ settings, onClose, onSave, userData, onProfilePictureUpload, onProfilePictureDelete }) {
  const [formData, setFormData] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [fileInputRef] = useState(React.createRef());
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [deletingProfilePicture, setDeletingProfilePicture] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [hasActiveConsent, setHasActiveConsent] = useState(null); // null = loading, true/false = loaded
  const [checkingConsent, setCheckingConsent] = useState(false);
  const [withdrawingConsent, setWithdrawingConsent] = useState(false);
  const [showWithdrawConsentConfirmation, setShowWithdrawConsentConfirmation] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    editProfile: false,
    healthConsent: false
  });
  const navigate = useNavigate();

  // Crop state
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const readFile = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result), false);
      reader.readAsDataURL(file);
    });
  };

  const handleSaveCroppedImage = async () => {
    try {
      setUploadingProfilePicture(true);
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      const file = new File([croppedImageBlob], "profile_picture.jpg", { type: "image/jpeg" });
      
      const fakeEvent = {
        target: {
          files: [file]
        }
      };
      
      await onProfilePictureUpload(fakeEvent);
      setImageSrc(null); // Close cropper
    } catch (e) {
      console.error('Crop/upload error:', sanitizeErrorForLog(e));
      alert('Failed to crop/upload image');
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  const handleCancelCrop = () => {
    setImageSrc(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  // Countries list (same as SignUp component)
  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
    'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
    'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
    'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
    'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador',
    'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
    'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
    'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait',
    'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico',
    'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru',
    'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
    'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
    'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
    'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu',
    'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(settings); // Reset to original values
    setShowDeleteConfirmation(false);
    onClose();
  };

  // Profile picture handlers
  const handleProfilePictureClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (onProfilePictureDelete) {
      setDeletingProfilePicture(true);
      try {
        await onProfilePictureDelete();
        setShowDeleteConfirmation(false);
      } finally {
        setDeletingProfilePicture(false);
      }
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear any cached data
      sessionStorage.clear();
      localStorage.clear();
      
      // Close modal first
      onClose();
      
      // Navigate to home
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', sanitizeErrorForLog(error));
    }
  };

  // Fetch consent status (returns boolean; no state - for dedupeRequest)
  const fetchConsentStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return false;

      const { data, error } = await supabase
        .from('health_data_consent')
        .select('id')
        .eq('user_id', userId)
        .eq('consent_type', 'health_biometric_processing')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error checking health data consent:', sanitizeErrorForLog(error));
        return false;
      }
      return !!data;
    } catch (error) {
      console.error('Error checking health data consent:', sanitizeErrorForLog(error));
      return false;
    }
  };

  // Check consent when modal opens (dedupe to avoid duplicate requests from React StrictMode)
  useEffect(() => {
    let mounted = true;
    setCheckingConsent(true);
    dedupeRequest('ProfileSettingsModal_checkActiveConsent', fetchConsentStatus)
      .then((result) => {
        if (mounted) {
          setHasActiveConsent(result);
          setCheckingConsent(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setHasActiveConsent(false);
          setCheckingConsent(false);
        }
      });
    return () => { mounted = false; };
  }, []);

  // Withdraw consent (use getSession to avoid extra GET /auth/v1/user)
  const handleWithdrawConsent = async () => {
    try {
      setWithdrawingConsent(true);
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        alert('Please sign in to withdraw consent');
        return;
      }

      // Find the active consent record
      const { data: activeConsent, error: findError } = await supabase
        .from('health_data_consent')
        .select('id')
        .eq('user_id', userId)
        .eq('consent_type', 'health_biometric_processing')
        .eq('is_active', true)
        .maybeSingle();

      if (findError) {
        console.error('Error finding active consent:', sanitizeErrorForLog(findError));
        alert('Error withdrawing consent. Please try again.');
        return;
      }

      if (!activeConsent) {
        alert('No active consent found');
        setHasActiveConsent(false);
        setShowWithdrawConsentConfirmation(false);
        return;
      }

      // Update the consent record to set withdrawn_at and withdrawal_method
      const { error: updateError } = await supabase
        .from('health_data_consent')
        .update({
          withdrawn_at: new Date().toISOString(),
          withdrawal_method: 'profile_settings'
        })
        .eq('id', activeConsent.id);

      if (updateError) {
        console.error('Error withdrawing consent:', updateError);
        alert('Error withdrawing consent. Please try again.');
        return;
      }

      // Delete all protocols created by the user
      const { error: deleteProtocolsError } = await supabase
        .from('protocols')
        .delete()
        .eq('created_by', userId);

      if (deleteProtocolsError) {
        console.error('Error deleting protocols:', sanitizeErrorForLog(deleteProtocolsError));
        // Continue even if protocol deletion fails - consent withdrawal succeeded
        // but log the error for debugging
      }

      // Remove all protocol associations (active/staged protocols they didn't create)
      const { error: deleteProtocolStatusError } = await supabase
        .from('protocols_status')
        .delete()
        .eq('user_id', userId);

      if (deleteProtocolStatusError) {
        console.error('Error deleting protocol statuses:', deleteProtocolStatusError);
        // Continue even if protocol status deletion fails - consent withdrawal succeeded
        // but log the error for debugging
      }

      // Get all panel IDs for the user to delete biomarker results
      const { data: userPanels, error: panelsError } = await supabase
        .from('biomarker_panel_results')
        .select('id')
        .eq('user_id', userId);

      if (panelsError) {
        console.error('Error fetching biomarker panels:', sanitizeErrorForLog(panelsError));
      } else if (userPanels && userPanels.length > 0) {
        const panelIds = userPanels.map(panel => panel.id);
        
        // Delete all biomarker results for user's panels
        const { error: deleteBiomarkerResultsError } = await supabase
          .from('biomarker_results')
          .delete()
          .in('panel_id', panelIds);

        if (deleteBiomarkerResultsError) {
          console.error('Error deleting biomarker results:', sanitizeErrorForLog(deleteBiomarkerResultsError));
          // Continue even if biomarker results deletion fails
        }
      }

      // Delete all biomarker panels for the user
      const { error: deleteBiomarkerPanelsError } = await supabase
        .from('biomarker_panel_results')
        .delete()
        .eq('user_id', userId);

      if (deleteBiomarkerPanelsError) {
        console.error('Error deleting biomarker panels:', sanitizeErrorForLog(deleteBiomarkerPanelsError));
        // Continue even if biomarker panel deletion fails - consent withdrawal succeeded
        // but log the error for debugging
      }

      // Delete all sleep sessions (both manual and connected wearables data)
      const { error: deleteSleepSessionsError } = await supabase
        .from('sleep_sessions')
        .delete()
        .eq('user_id', userId);

      if (deleteSleepSessionsError) {
        console.error('Error deleting sleep sessions:', sanitizeErrorForLog(deleteSleepSessionsError));
        // Continue even if sleep sessions deletion fails
      }

      // Delete all Fitbit activity data
      const { error: deleteFitbitActivitiesError } = await supabase
        .from('fitbit_activities')
        .delete()
        .eq('user_id', userId);

      if (deleteFitbitActivitiesError) {
        console.error('Error deleting Fitbit activities:', sanitizeErrorForLog(deleteFitbitActivitiesError));
        // Continue even if Fitbit activities deletion fails
      }

      // Delete all Fitbit heart rate data
      const { error: deleteFitbitHeartRateError } = await supabase
        .from('fitbit_heart_rate')
        .delete()
        .eq('user_id', userId);

      if (deleteFitbitHeartRateError) {
        console.error('Error deleting Fitbit heart rate:', sanitizeErrorForLog(deleteFitbitHeartRateError));
        // Continue even if Fitbit heart rate deletion fails
      }

      // Delete all Garmin raw data
      const { error: deleteGarminRawDataError } = await supabase
        .from('garmin_raw_data')
        .delete()
        .eq('user_id', userId);

      if (deleteGarminRawDataError) {
        console.error('Error deleting Garmin raw data:', sanitizeErrorForLog(deleteGarminRawDataError));
        // Continue even if Garmin raw data deletion fails
      }

      // Delete all wearable connections (disconnect all devices)
      const { error: deleteWearableConnectionsError } = await supabase
        .from('wearable_connections')
        .delete()
        .eq('user_id', userId);

      if (deleteWearableConnectionsError) {
        console.error('Error deleting wearable connections:', sanitizeErrorForLog(deleteWearableConnectionsError));
        // Continue even if wearable connections deletion fails
      }

      // Delete all protocol report snapshots
      const { error: deleteProtocolReportsError } = await supabase
        .from('protocol_report_snapshots')
        .delete()
        .eq('user_id', userId);

      if (deleteProtocolReportsError) {
        console.error('Error deleting protocol reports:', sanitizeErrorForLog(deleteProtocolReportsError));
        // Continue even if protocol reports deletion fails
      }

      // Clean up orphaned post attachments
      // Find all post_attachments on user's posts that have no remaining content
      // (no protocols, no reports, no biomarkers, no images)
      // Note: RLS policies allow users to read/delete attachments on their own posts
      // After protocols/reports are deleted, CASCADE removes the related attachment records,
      // so nested queries will return empty arrays, which we use to identify empty attachments
      const { data: userPosts, error: postsError } = await supabase
        .from('posts')
        .select('id')
        .eq('user_id', userId);

      if (postsError) {
        console.error('Error fetching user posts:', sanitizeErrorForLog(postsError));
      } else if (userPosts && userPosts.length > 0) {
        const postIds = userPosts.map(post => post.id);
        
        // Get all post_attachments for user's posts
        // RLS: Users can view attachments on their own posts (setup-post-attachments-rls.sql)
        const { data: postAttachments, error: attachmentsError } = await supabase
          .from('post_attachments')
          .select(`
            id,
            post_attachment_protocols:post_attachment_protocols_post_attachment_id_fkey(id),
            post_attachment_reports:post_attachment_reports_post_attachment_id_fkey(id),
            post_attachment_biomarkers:post_attachment_biomarkers_post_attachment_id_fkey(id),
            post_attachment_images:post_attachment_images_post_attachment_id_fkey(id)
          `)
          .in('post_id', postIds)
          .eq('is_deleted', false);

        if (attachmentsError) {
          console.error('Error fetching post attachments:', sanitizeErrorForLog(attachmentsError));
          // If RLS blocks the query, log it but continue (cleanup is non-critical)
        } else if (postAttachments && postAttachments.length > 0) {
          // Find attachments with no remaining content
          // After CASCADE deletes, nested arrays will be empty or undefined
          const emptyAttachmentIds = postAttachments
            .filter(att => {
              const hasProtocols = att.post_attachment_protocols && att.post_attachment_protocols.length > 0;
              const hasReports = att.post_attachment_reports && att.post_attachment_reports.length > 0;
              const hasBiomarkers = att.post_attachment_biomarkers && att.post_attachment_biomarkers.length > 0;
              const hasImages = att.post_attachment_images && att.post_attachment_images.length > 0;
              return !hasProtocols && !hasReports && !hasBiomarkers && !hasImages;
            })
            .map(att => att.id);

          // Delete empty attachments
          // RLS: Users can delete attachments on their own posts (setup-post-attachments-rls.sql)
          if (emptyAttachmentIds.length > 0) {
            const { error: deleteEmptyAttachmentsError } = await supabase
              .from('post_attachments')
              .delete()
              .in('id', emptyAttachmentIds);

            if (deleteEmptyAttachmentsError) {
              console.error('Error deleting empty post attachments:', sanitizeErrorForLog(deleteEmptyAttachmentsError));
              // Continue even if cleanup fails - this is a cleanup operation, not critical
            }
          }
        }
      }

      // Update local state
      setHasActiveConsent(false);
      setShowWithdrawConsentConfirmation(false);
      
      // Show success message
      alert('Consent withdrawn successfully');
    } catch (error) {
      console.error('Error withdrawing consent:', sanitizeErrorForLog(error));
      alert('Error withdrawing consent. Please try again.');
    } finally {
      setWithdrawingConsent(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="profile-settings-modal" onClick={e => e.stopPropagation()}>
        {imageSrc ? (
          <div className="crop-container-wrapper">
             <div className="crop-header" style={{ padding: '20px 24px', borderBottom: '1px solid var(--color-border)' }}>
               <h3>Adjust Profile Picture</h3>
               <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                 Drag to position and use slider to zoom
               </p>
             </div>
             <div className="crop-container" style={{ position: 'relative', height: '300px', width: '100%', backgroundColor: '#000' }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="round"
                showGrid={false}
              />
            </div>
            <div className="crop-controls" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="zoom-control" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon name="Minus" className="icon--xs" />
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(e.target.value)}
                  className="zoom-range"
                  style={{ flex: 1 }}
                />
                <Icon name="Plus" className="icon--xs" />
              </div>
              <div className="crop-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '0' }}>
                 <button 
                  className="btn btn--secondary btn--md"
                  onClick={handleCancelCrop}
                  disabled={uploadingProfilePicture}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn--primary btn--md"
                  onClick={handleSaveCroppedImage}
                  disabled={uploadingProfilePicture}
                >
                  {uploadingProfilePicture ? 'Saving...' : 'Save Picture'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
        <div className="profile-settings-header">
          <div className="profile-settings-heading">
            <h2>Settings</h2>
            <p>Manage your profile and health data consent.</p>
          </div>
          <button className="close-btn" onClick={handleCancel}>
            <Icon name="X" className="icon--sm" />
          </button>
        </div>
        
        <div className="profile-settings-content">
          {/* Edit Profile Section */}
          <div className="settings-section">
            <div 
              className={`settings-section-header ${expandedSections.editProfile ? 'expanded' : ''}`}
              onClick={() => setExpandedSections(prev => ({ ...prev, editProfile: !prev.editProfile }))}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3>Edit Profile</h3>
                  <p>Update your public info and avatar.</p>
                </div>
                <Icon 
                  name="ChevronDown" 
                  className="icon--sm" 
                  style={{ 
                    transform: expandedSections.editProfile ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </div>
            </div>
            {expandedSections.editProfile && (
              <div className="profile-settings-form">
              <div className="form-group">
                <label>Profile Picture</label>
                <div className="profile-picture-section-new">
                  <div className="profile-picture-preview">
                    {userData?.profile_picture_url ? (
                      <img 
                        src={userData.profile_picture_url} 
                        alt="Profile" 
                        className="profile-picture-image"
                      />
                    ) : (
                      <div className="profile-picture-placeholder">
                        {userData?.username?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )}
                    {uploadingProfilePicture && (
                      <div className="profile-picture-overlay">
                        <JellyfishLoader strokeColor={getCSSVar('--color-text-on-primary')} />
                      </div>
                    )}
                  </div>
                  <div className="profile-picture-buttons">
                    <button 
                      type="button"
                      className="btn btn--primary btn--sm profile-picture-btn"
                      onClick={handleProfilePictureClick}
                      disabled={uploadingProfilePicture}
                    >
                      <Icon name="Upload" className="icon--xs" />
                      {uploadingProfilePicture ? 'Uploading...' : 'Upload'}
                    </button>
                    <button 
                      type="button"
                      className="btn btn--danger btn--sm profile-picture-btn"
                      onClick={() => setShowDeleteConfirmation(true)}
                      disabled={deletingProfilePicture}
                    >
                      <Icon name="Trash2" className="icon--xs" />
                      {deletingProfilePicture ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  placeholder="Enter your username"
                  maxLength={50}
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country</label>
                <select
                  id="country"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                >
                  <option value="">Select a country</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email"
                  disabled // Email changes typically require verification
                />
                <small className="form-help">Email changes require verification. Contact support if needed.</small>
              </div>
              </div>
            )}
          </div>

          {/* Health Data Consent Section */}
          <div className="settings-section">
            <div 
              className={`settings-section-header ${expandedSections.healthConsent ? 'expanded' : ''}`}
              onClick={() => setExpandedSections(prev => ({ ...prev, healthConsent: !prev.healthConsent }))}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3>Health Data Consent</h3>
                  <p>Manage your consent for health and biometric data processing.</p>
                </div>
                <Icon 
                  name="ChevronDown" 
                  className="icon--sm" 
                  style={{ 
                    transform: expandedSections.healthConsent ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </div>
            </div>
            {expandedSections.healthConsent && (
              <div className="form-group">
                {checkingConsent ? (
                  <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Checking consent status...</p>
                ) : hasActiveConsent ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: '1.5' }}>
                      You have given consent for health and biometric data processing. You can withdraw this consent at any time.
                    </p>
                    <button
                      type="button"
                      className="health-consent-withdraw-btn"
                      onClick={() => setShowWithdrawConsentConfirmation(true)}
                      disabled={withdrawingConsent}
                    >
                      {withdrawingConsent ? 'Processing...' : 'Withdraw Consent'}
                    </button>
                  </div>
                ) : (
                  <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: '1.5' }}>
                    You have not given consent for health and biometric data processing. You can provide consent when creating protocols.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="profile-settings-footer profile-settings-footer--combined">
          <button 
            className="btn btn--md profile-settings-sign-out"
            onClick={handleSignOut}
          >
            <Icon name="LogOut" className="icon--sm" />
            <span>Sign Out</span>
          </button>
          <div className="profile-settings-footer-actions">
            <button 
              className="btn btn--secondary btn--md"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button 
              className="btn btn--primary btn--md"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirmation && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirmation(false)}>
            <div className="delete-confirmation-modal-new" onClick={e => e.stopPropagation()}>
              <h3>Remove Profile Picture?</h3>
              <p>This will remove your current profile picture and reset it to the default avatar. This action cannot be undone.</p>
              
              <div className="delete-confirmation-actions-new">
                <button 
                  className="btn btn--secondary btn--md"
                  onClick={() => setShowDeleteConfirmation(false)}
                  disabled={deletingProfilePicture}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn--danger btn--md"
                  onClick={handleDeleteProfilePicture}
                  disabled={deletingProfilePicture}
                >
                  {deletingProfilePicture ? 'Removing...' : 'Remove Picture'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Withdraw Consent Confirmation Dialog */}
        {showWithdrawConsentConfirmation && (
          <div className="modal-overlay" onClick={() => setShowWithdrawConsentConfirmation(false)}>
            <div className="delete-confirmation-modal-new" onClick={e => e.stopPropagation()}>
              <h3>Withdraw Health Data Consent?</h3>
              <p>This will withdraw your consent for health and biometric data processing. You will no longer be able to create protocols or use features that require this consent. You can provide consent again at any time.</p>
              <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: '0 0 2rem 0', textAlign: 'center', lineHeight: '1.5', fontWeight: '500' }}>
                <strong>Warning:</strong> This will permanently delete all of your health data, including:
                <br />• All protocols you created
                <br />• All active and staged protocols (even ones you didn't create)
                <br />• All biomarker data and lab results
                <br />• All wearables data (manual entries and connected device data)
                <br />• All protocol reports
                <br />• All related post attachments
                <br /><br />This action cannot be undone.
              </p>
              
              <div className="delete-confirmation-actions-new">
                <button 
                  className="btn btn--secondary btn--md"
                  onClick={() => setShowWithdrawConsentConfirmation(false)}
                  disabled={withdrawingConsent}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn--danger btn--md"
                  onClick={handleWithdrawConsent}
                  disabled={withdrawingConsent}
                >
                  {withdrawingConsent ? 'Processing...' : 'Withdraw'}
                </button>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>
      <BottomNavigation />
    </div>
  );
}

export default UserPage;